import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { API } from "@/lib/api";
import AmbientMixer from "@/lib/ambientMixer";
import { setSessionMetadata, clearSessionMetadata, bindMediaSessionControls, attachAmbientStreamToAudio } from "@/lib/mediaSession";
import { startKeepAlive, stopKeepAlive } from "@/lib/silentKeepAlive";
import { retryOnNextGesture } from "@/lib/audioUnlock";
import { useForegroundService } from "@/hooks/useForegroundService";
import { STEPPED_AWAY_COPY } from "@/lib/steppedAwayCopy";
import { attachDialogueFades } from "@/lib/dialogueFade";
import { closeHostedSession, setSessionEnded as setGlobalSessionEnded } from "@/lib/sessionHost";
import { toast } from "sonner";
import { Moon, Sun, Sparkles, Pause, Play, Check } from "lucide-react";
import { BottomNav, TopBar } from "@/components/AppShell";
import SettingsDrawer from "@/components/SettingsDrawer";
import MorningFeedback from "@/components/MorningFeedback";

// ITERATION 50 — PIPELINE TRACE.
// Always-on. Produces a single continuous, timestamped record of the session
// opening in the browser DevTools console — no flag needed. This exists so
// we can pinpoint the exact step where "ambience started but Serine never
// spoke" is happening on real devices.
//
// HOW TO USE:
//   1. Open DevTools → Console tab BEFORE tapping Play (or refresh with it open).
//   2. Start a session normally.
//   3. When something is wrong, copy the whole console log — every line is
//      prefixed with `[continua +XXXXms] [CATEGORY]` for easy filtering /
//      searching. Send the log to us.
//
// Category legend:
//   [SESSION]  — high-level lifecycle beats (create, arrived, ended, error)
//   [AMBIENT]  — ambient mixer + hidden <audio> mirror + world events
//   [LLM]      — /sessions/generate call + returned metadata
//   [TTS]      — /sessions/:id/render_audio + audio URL received
//   [PLAYER]   — <audio> element events (loadeddata / canplay / play / pause / ended / error)
//   [PRESENCE] — companion presence + scenario scheduler arming + heartbeat
//   [WATCHDOG] — 150s init timeout arm / clear / fire
const __sessionT0 = (typeof performance !== "undefined") ? performance.now() : Date.now();
const __ts = () => {
  const now = (typeof performance !== "undefined") ? performance.now() : Date.now();
  return `+${Math.round(now - __sessionT0)}ms`.padStart(8, " ");
};
const strace = (cat, ...args) => {
  // eslint-disable-next-line no-console
  console.info(`[continua ${__ts()}] [${cat}]`, ...args);
};
// Retain the debug-only dlog for extra-verbose branches; the main trace is now always on.
const dlog = (...args) => {
  if (typeof window !== "undefined" && window.__continuaDebug) {
    // eslint-disable-next-line no-console
    console.info("[continua-dbg]", ...args);
  }
};

// ============================================================================
// Iter 125 — SESSION-START HARDENING.
// On iOS/TestFlight the first session start could require app restarts:
//   1) wall-clock poll deadlines (Date.now() + N) expired while the OS
//      suspended JS during the 30-90s generation — on resume the code
//      instantly declared a timeout even though the session was READY
//      server-side (restarting the app then attached to it, which is why
//      restarts "fixed" it);
//   2) the initial POST /sessions/generate had no timeout and no retry — one
//      network blip on a cold radio = failed night.
// makeActiveClock counts only FOREGROUND time against a budget; postWithRetry
// retries transient failures with backoff (idempotent server-side via
// client_request_id); findOrphanSession adopts a session whose create-POST
// landed but whose response was lost.
// ============================================================================
const makeActiveClock = (budgetMs) => {
  let last = Date.now();
  let remaining = budgetMs;
  return {
    tick() {
      const now = Date.now();
      const gap = now - last;
      last = now;
      // A gap >10s means the OS froze us (lock screen / backgrounding).
      // Suspended time never counts against the budget — the backend kept
      // working the whole time.
      if (gap < 10000) remaining -= gap;
      return remaining > 0;
    },
    get remainingMs() { return remaining; },
  };
};

const postWithRetry = async (url, payload, { tries = 3, timeoutMs = 25000, label = "request", trace = () => {} } = {}) => {
  let lastErr = null;
  for (let attempt = 1; attempt <= tries; attempt += 1) {
    try {
      return await api.post(url, payload, { timeout: timeoutMs });
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      // Real 4xx (auth/validation/premium gates) are permanent — never retry.
      if (status && status < 500 && status !== 408 && status !== 429) throw err;
      trace(`${label} attempt ${attempt}/${tries} failed — ${status ? `HTTP ${status}` : "network/timeout"}`, {
        status: status || null,
        message: err?.message,
      });
      if (attempt < tries) await new Promise((r) => setTimeout(r, attempt * 2000));
    }
  }
  throw lastErr;
};

const findOrphanSession = async (clientRequestId) => {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const { data } = await api.get("/sessions", { timeout: 15000 });
      const cand = (data || []).find((s) =>
        s.kind === "evening" &&
        (s.status === "generating" || s.status === "ready") &&
        (s.client_request_id
          ? s.client_request_id === clientRequestId
          : Date.now() - new Date(s.created_at).getTime() < 3 * 60 * 1000)
      );
      return cand || null;
    } catch (e) {
      await new Promise((r) => setTimeout(r, 2500));
    }
  }
  return null;
};

const PREPARATION_TIPS = [
  "Make sure you have finished everything before the session begins.",
  "Silence notifications if you can — one buzz can undo ten quiet minutes.",
  "Get comfortable. Adjust the pillow now, not later.",
  "Headphones bring the presence closer — but a quiet speaker works too.",
  "Set your volume now. The session begins softly.",
  "Dim the lights if you would like.",
  "Plug in your phone if the night will be long.",
  "You can lock your phone — the session keeps playing.",
  "Take a slow breath.",
];

const COMPANION_NAME_FALLBACKS = {
  seriny: "Serine",
  serine: "Serine",
  elizabeth: "Elizabeth",
};

// Iter 87 — session reflection lives directly under the completed session
// (moved here from the separate SessionPlayer screen).
const FEEDBACK_TAGS = [
  { id: "more_atmosphere", label: "More atmosphere" },
  { id: "more_conversation", label: "More conversation" },
  { id: "more_silence", label: "More silence" },
  { id: "more_comfort", label: "More comfort" },
  { id: "more_adventure", label: "More adventure" },
  { id: "more_storytelling", label: "More storytelling" },
  { id: "slower_pace", label: "Speak more slowly" },
  { id: "more_pauses", label: "More pauses" },
  { id: "calmer_voice", label: "Calmer voice" },
];

/**
 * PERSONALITY-DRIVEN REACTION BANKS.
 *
 * The SAME world event produces DIFFERENT emotional responses depending on WHO
 * the companion is. The event is only a trigger; personality decides whether it
 * is noticed (probability) and how it is interpreted (lines). Reactions are
 * RARE — most events pass without a word.
 *
 *   World Event → Character Personality → Probability → Reaction
 */
const REACTION_BANKS = {
  seriny: {
    // Serine is uneasy about storms — she notices thunder far more than others.
    thunder: { p: 0.6, lines: ["...I don't like storms very much.", "Come a little closer. The thunder makes me uneasy.", "Mm. That one was close."] },
    owl: { p: 0.22, lines: ["The owls are awake too.", "...listen. Just for a moment."] },
    fire: { p: 0.18, lines: ["The fire's good tonight.", "Warm enough?"] },
    rain: { p: 0.18, lines: ["The rain hasn't let up.", "It's cozy in here, though."] },
    idle: { p: 0.16, lines: ["You can stay close if you'd like.", "I'm right here.", "...I'm glad you came.", "Rest. I've got you."] },
  },
  elizabeth: {
    // Elizabeth is unbothered by storms — she may even enjoy them.
    thunder: { p: 0.3, lines: ["I've always liked the sound of a storm.", "Mm. Let it come."] },
    rain: { p: 0.35, lines: ["I've always liked the sound of rain.", "Perfect weather for staying in."] },
    owl: { p: 0.15, lines: ["Something's awake out there."] },
    fire: { p: 0.25, lines: ["The fire suits you.", "Stay by it."] },
    idle: { p: 0.15, lines: ["No need to say anything.", "This is enough.", "I like the quiet with you."] },
  },
};

const DEFAULT_REACTION_BANK = {
  thunder: { p: 0.3, lines: ["Mm. Thunder."] },
  rain: { p: 0.2, lines: ["Listen to the rain."] },
  idle: { p: 0.14, lines: ["I'm here.", "Stay as long as you like."] },
};

function bankForCharacter(characterId) {
  return REACTION_BANKS[characterId] || REACTION_BANKS[COMPANION_NAME_FALLBACKS[characterId]?.toLowerCase()] || DEFAULT_REACTION_BANK;
}

// Map a mixer world-event category to a reaction trigger key.
function triggerForCategory(category) {
  const c = String(category || "").toLowerCase();
  if (c.includes("thunder")) return "thunder";
  if (c.includes("owl")) return "owl";
  if (c.includes("fire") || c.includes("ember") || c.includes("camp")) return "fire";
  if (c.includes("rain")) return "rain";
  if (c.includes("ocean") || c.includes("wave")) return "ocean";
  return null;
}

/**
 * Arrival (formerly the "waiting room").
 *
 * The user pressed "Step inside". They never see a generation workflow —
 * they arrive at the place. Ambient starts within ~100ms. LLM + ElevenLabs
 * happen invisibly in the background. The companion's voice fades in over
 * 1.5s when ready, on top of the ambient that has been there the whole time.
 *
 * Pause/resume stops/resumes BOTH layers. No narration text is shown.
 */
export default function WaitingRoom({ hostParams = null }) {
  const nav = useNavigate();
  const loc = useLocation();
  // Iter 86 — when hosted by SessionHost the launch params are pinned by the
  // host (they must survive route changes); route state remains a fallback
  // for any legacy entry path.
  const params = hostParams || loc.state || {};
  // Iter 102 — foreground service ownership moved to a dedicated hook
  // (single home for the native import + guaranteed unmount cleanup).
  const fgService = useForegroundService();

  const mixerRef = useRef(null);
  const audioRef = useRef(null);
  const ambientAudioRef = useRef(null);
  const fadeRef = useRef(null);
  const endFadeTimerRef = useRef(null);
  const triedGenRef = useRef(false);
  const triedAmbientRef = useRef(false);
  // Session runtime — the session is the DURATION, not the speech.
  const sessionTimerRef = useRef(null);
  const sessionEndsAtRef = useRef(null);
  const remainingMsRef = useRef(null);
  const durationMsRef = useRef(((hostParams || loc.state)?.durationMinutes || 15) * 60 * 1000);
  const pausedRef = useRef(false);
  // Iter 118 — SESSION LIFECYCLE.
  //
  // A session pauses for exactly TWO different reasons and they must be
  // treated very differently. Continua promises that a session survives
  // in-app navigation, screen locks, backgrounding, brief OS interruptions
  // — anything short of an explicit user "pause / stop / end" is a
  // recoverable interruption, not an ending.
  //
  //   userInitiatedPauseRef = true
  //     The user (or lock-screen media control) explicitly asked to pause.
  //     Playback stays paused until they resume; the auto-recovery loop
  //     leaves it alone.
  //
  //   userInitiatedPauseRef = false + pausedRef = true
  //     The OS / browser suspended playback on us (iOS backgrounding, tab
  //     hidden, audio session interruption, HTMLAudioElement pause). The
  //     visibility / app-active listeners below will attempt to resume the
  //     moment the tab is foregrounded again.
  const userInitiatedPauseRef = useRef(false);
  const endedRef = useRef(false);
  // Reactions — personality-driven, probabilistic, rare.
  const reactionAudioRef = useRef(null);
  const reactionBankRef = useRef(null);     // {trigger: {p, urls:[...]}}
  const reactionCooldownRef = useRef(0);     // Date.now() until which no reaction fires
  const idleTimerRef = useRef(null);
  const reactionsReadyRef = useRef(false);
  const reactionBiasRef = useRef(1); // mood/need multiplier on reaction rarity
  // Live reactive-dialogue generation. Backed by /sessions/:id/react — the
  // companion produces FRESH short lines throughout the session in response
  // to world events + long silences (density scaled by session length).
  const sessionIdRef = useRef(null);
  const reactiveInFlightRef = useRef(false);
  // Iteration 57 — Reactive TTS pipeline serialization.
  //
  // Only ONE ElevenLabs TTS may run for a given session at a time. The
  // initial monologue has the highest priority: while it is generating OR
  // still on the polling path, we drop every reactive request with a clean
  // [REACTIVE_TTS] skipped: initial_pending trace. Once the main dialogue
  // audio has started playing for the user (dialogueStartedRef=true), the
  // lock is released and a FIFO queue of pending world events / idle beats
  // begins draining ONE-AT-A-TIME.
  //
  //   ttsLockRef.current: null | 'initial' | 'reactive'
  //     null      → free; the queue processor may pull the next event
  //     'initial' → the opening monologue owns the pipeline (generation or playback)
  //     'reactive'→ a single reactive line is being generated or played
  //
  // Reactive lines NEVER overlap: each waits for the previous audio's onended
  // before starting. Stale queued events (>45s old) are dropped before pulling.
  const ttsLockRef = useRef("initial");            // starts locked to the opening
  const reactiveQueueRef = useRef([]);              // FIFO: [{trigger, event_category, enqueued_at}]
  const reactiveProcessingRef = useRef(false);      // reentrancy guard for the drain loop
  const REACTIVE_QUEUE_MAX = 6;
  const REACTIVE_STALE_MS = 45000;
  const rtrace = (state, extra) => {
    // eslint-disable-next-line no-console
    console.info(`[continua ${__ts()}] [REACTIVE_TTS] ${state}`, extra || "");
  };
  // AUDIO_PLAY audit — the authoritative record of EVERYTHING spoken.
  // Every path that plays companion speech (opening clusters, timeline
  // segments, canned/presence reaction bank, live reactive lines) MUST route
  // its playback moment through logAudioPlay so the console trace + master
  // timeline capture the exact text, source and inter-speech gap.
  const lastSpokenPlayRef = useRef(0);
  const logAudioPlay = (source, text, { audio_hash = null, push = true } = {}) => {
    const now = Date.now();
    const elapsed = lastSpokenPlayRef.current
      ? Math.round((now - lastSpokenPlayRef.current) / 100) / 10
      : null;
    lastSpokenPlayRef.current = now;
    // eslint-disable-next-line no-console
    console.info(
      `[continua ${__ts()}] [AUDIO_PLAY] source=${source} elapsed_since_previous=${elapsed == null ? "first" : `${elapsed}s`} text="${(text || "").slice(0, 120)}"`,
      { audio_hash }
    );
    if (push) {
      try {
        timelineEventsRef.current.push({
          type: "reactive",
          at_sec: _now_at_sec(),
          audio_hash: audio_hash || null,
          url: null,
          text: (text || "").slice(0, 200),
          source,
        });
      } catch (e) { /* ignore */ }
    }
  };
  const _hashFromAudioUrl = (url) => ((String(url || "").match(/\/audio\/([a-f0-9]+)\.mp3/) || [])[1] || null);
  // Phase 3 — Hybrid Master Rendering timeline capture.
  // Every meaningful event during the live session is recorded (with its
  // real at_sec offset from session start) so /api/sessions/{id}/finalize
  // can render ONE deterministic MP3 that exactly matches what the user
  // just heard. See master_renderer.py for the render side.
  const sessionStartMsRef = useRef(null);        // performance.now() at ambient start
  const dialogueAudioHashRef = useRef(null);     // from /render_audio response
  const dialogueAtSecRef = useRef(null);         // at_sec when onAudioPlay fires
  const ambientLoopUrlsRef = useRef([]);         // resolved from audio_pack
  const timelineEventsRef = useRef([]);          // [{type, at_sec, audio_hash?, url?, category?}]
  const finalizedRef = useRef(false);
  const _now_at_sec = () => {
    if (sessionStartMsRef.current == null) return 0;
    const now = (typeof performance !== "undefined") ? performance.now() : Date.now();
    return Math.max(0, (now - sessionStartMsRef.current) / 1000);
  };
  // Iteration 58 — Action Director cues.
  // Populated when /sessions/generate returns action_cues[] (backend-planned
  // SFX for phrases like "let me pour some tea"). When the initial dialogue
  // MP3 loads and we know its true duration, we compute each cue's absolute
  // at_sec = text_offset_ratio × dialogue_audio_duration_sec + dialogueAtSec
  // and schedule the SFX play via setTimeout relative to the current
  // audioRef.currentTime. Also mirrored into the master timeline so replays
  // stay perfectly in sync via the Phase 3 renderer.
  const actionCuesRef = useRef([]);           // [{phrase, category, sfx_url, sfx_duration_sec, pause_sec, text_offset_ratio}]
  const actionCueTimersRef = useRef([]);      // setTimeout IDs to clear on pause/end
  const actionCuePlayedRef = useRef(new Set()); // idempotency guard so pause/resume never plays twice
  // Iteration 59 — Fully-authored evening pipeline.
  // When session.dialogue_timeline is present (and segments are pre-rendered),
  // playback is a deterministic scheduler: play each segment at its at_sec,
  // no runtime reactive LLM/TTS calls. The reactive queue path becomes a no-op.
  const dialogueTimelineRef = useRef(null);   // [{at_sec, text, kind, audio_hash, audio_url, action_cues?}]
  const segmentTimersRef = useRef([]);        // setTimeout IDs to cancel on pause/end
  const segmentPlayedRef = useRef(new Set()); // idempotency guard
  const hasFullTimelineRef = useRef(false);   // true → runtime is deterministic; reactive queue disabled
  // Iteration 63 — Visual Presence Cue.
  // When a scheduled dialogue segment is ~2s away, the arrival-orb brightens
  // so the listener subconsciously registers that the companion is about to
  // speak. Cleared automatically ~8s after each segment fires (soft fade back
  // to base). Deterministic thanks to iter 59's known timeline.
  const [presenceCueActive, setPresenceCueActive] = useState(false);
  const presenceCueTimersRef = useRef([]);
  const PRESENCE_LEAD_MS = 2000;          // brighten this long BEFORE the segment plays
  const PRESENCE_HOLD_MS = 8000;           // hold brightness for this long AFTER firing
  // Iteration 82 — Human Performance Direction.
  // Sleep-mode openings arrive SPLIT into thought clusters (kind 'opening'
  // + chained 'opening_cont' segments). Cluster 1 plays through the main
  // <audio> element; the rest are CHAINED: each waits its `gap_before_sec`
  // of REAL silence (ambience only) after the previous cluster ends, then
  // plays as its own Audio element. The normal "opening finished" hand-off
  // only happens after the LAST cluster.
  const openingChainRef = useRef([]);        // opening_cont segments, cluster order
  const openingChainIndexRef = useRef(0);    // next cluster to play
  const openingChainStateRef = useRef("idle"); // idle | waiting | playing | done
  // Iter 82 — dialogue sits slightly under full volume so the atmosphere
  // remains the primary layer. The world keeps breathing beneath her voice.
  // Iter 90 — default mix: character voice 40%, atmosphere 50%.
  const DIALOGUE_VOLUME = 0.09; // Iter 105 — unified whisper level, ~45-50% vs ambience (was 0.11)
  // Track how many live reactive lines have actually played this session so we
  // can guarantee at least ONE lives-in mid-to-late reaction — the companion
  // must never feel like she disappeared after the opening.
  const liveReactionCountRef = useRef(0);
  const guaranteedReactionTimerRef = useRef(null);
  // Probability that a triggered reaction escalates to a LIVE LLM generation
  // (as opposed to the canned bank). Scaled with session length below.
  const liveReactionProbabilityRef = useRef(0.55);
  // Heartbeat — conditional; only when tonight's scene is a close chest-resting
  // embrace (backend sets evening.heartbeat), started after the opening monologue.
  const heartbeatEnabledRef = useRef(false);
  const heartbeatUrlRef = useRef(null);
  // Presence + scenario asset packs are cached from the audio_pack fetch and
  // ONLY installed on the mixer once dialogue actually begins. Keeps hissing /
  // slithering / heartbeat gated behind a real, successful arrival.
  const pendingPresenceRef = useRef(null);
  const pendingScenarioRef = useRef(null);
  const presenceStartedRef = useRef(false);
  const morningTimersRef = useRef([]); // Iter 92 — pending night→morning crossfade timers
  const morningEmbraceReleasedRef = useRef(false); // Iter 94 — heartbeat ends with the embrace, not a clock

  // Iter 96 polish — one-time reassurance that locking the phone is safe.
  const [showLockHint, setShowLockHint] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem("continua_lockscreen_hint_seen")) {
        setShowLockHint(true);
        localStorage.setItem("continua_lockscreen_hint_seen", "1");
        const t = setTimeout(() => setShowLockHint(false), 15000);
        return () => clearTimeout(t);
      }
    } catch (e) { /* ignore */ }
    return undefined;
  }, []);

  const [character, setCharacter] = useState(null);
  const [memoryTitle, setMemoryTitle] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(true);
  const [phase, setPhase] = useState("arriving"); // arriving | settled | playing | error
  const [sessionEnded, setSessionEnded] = useState(false);
  // Iter 87 — inline session reflection (shown under the completed session).
  const [feedback, setFeedback] = useState([]);
  const [feedbackSent, setFeedbackSent] = useState(false);
  // Continua 1.1 — experimental Morning Session feedback (policy-driven).
  const [morningFeedbackDue, setMorningFeedbackDue] = useState(false);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tipIndex, setTipIndex] = useState(0);
  const [tipOpacity, setTipOpacity] = useState(1);
  // If browser autoplay policy rejects the initial dialogue play(), we show a
  // VISIBLE "Tap to begin" button rather than a subtle toast the user might miss.
  const [needsUserGesture, setNeedsUserGesture] = useState(false);
  // Initialization watchdog — flips true the FIRST time dialogue audio actually
  // reaches the speakers. If we never reach that state within the watchdog
  // window, we surface the in-universe "she stepped away" screen and bail out.
  const dialogueStartedRef = useRef(false);
  const watchdogRef = useRef(null);
  const playRetryRef = useRef(null);
  const swapTimeoutRef = useRef(null);
  // On any recoverable init failure we report the atmosphere to the backend so
  // the NEXT successful session can open with a small in-character apology.
  const failureReportedRef = useRef(false);

  const atmoName = params.atmoName || "the fire";
  const isMorning = params.kind === "morning"; // Iter 91 — user-initiated Morning Session

  // Continua 1.1 — after a Morning Session ends, ask the backend whether the
  // lightweight feedback prompt is due (currently: after the first morning).
  useEffect(() => {
    if (!sessionEnded || !isMorning) return;
    api.get("/morning/feedback-status")
      .then((r) => setMorningFeedbackDue(!!r.data?.due))
      .catch(() => setMorningFeedbackDue(false));
  }, [sessionEnded, isMorning]);
  // Iter 92 — reverse-structure morning: ~55s of night ambience + presence,
  // then the world crossfades into morning; her first words wait until ~65s.
  const MORNING_CROSSFADE_AT_MS = 55000;
  const MORNING_OPENING_GATE_MS = 65000;
  // Iter 94 — the embrace ends when SHE moves away in the dialogue, not at a
  // timestamp. These phrases mark her naturally loosening the hold / getting up.
  const MORNING_RELEASE_RE = /(loosen|unwind(?:s|ing)?|uncoil|slip(?:s|ping)? (?:out|away|from)|let me (?:get up|stretch|sit up|move|rise)|i(?:'ll| will| should) (?:get up|stretch|rise|sit up|get going)|open the curtains|(?:start|begin) (?:my|the|my own|our) (?:day|morning)|time (?:for me )?to get up|out of bed|make (?:(?:us|you|some|a|little)\s+)*(?:tea|coffee|breakfast))/i;
  const targetVolume = DIALOGUE_VOLUME; // Iter 105 — the first opening cluster used to blast at 0.85 while every later segment played at 0.11; ONE whisper level everywhere now
  const companionName = character?.name || COMPANION_NAME_FALLBACKS[params.characterId] || "Someone";

  useEffect(() => {
    // Two-phase crossfade with a real gap between tips so they can never
    // visually overlap: fade to 0 (700ms) → wait 200ms → swap text → fade
    // back to 1 (700ms). Total cycle 8s. ALL nested timeouts are stored in
    // refs so React StrictMode's double-mount cleanup can cancel them fully.
    const FADE_MS = 700;
    const HOLD_MS = 200;
    const timer = setInterval(() => {
      setTipOpacity(0);
      if (swapTimeoutRef.current) clearTimeout(swapTimeoutRef.current);
      swapTimeoutRef.current = setTimeout(() => {
        setTipIndex((i) => (i + 1) % PREPARATION_TIPS.length);
        setTipOpacity(1);
        swapTimeoutRef.current = null;
      }, FADE_MS + HOLD_MS);
    }, 8000);
    return () => {
      clearInterval(timer);
      if (swapTimeoutRef.current) { clearTimeout(swapTimeoutRef.current); swapTimeoutRef.current = null; }
    };
  }, []);

  // Report a recoverable init failure so the NEXT successful generate opens
  // with an in-character apology tailored to tonight's atmosphere.
  const reportInitFailure = async (reason) => {
    if (failureReportedRef.current) return;
    failureReportedRef.current = true;
    try {
      await api.post("/sessions/report-failure", {
        atmosphere_id: params.atmosphereId || null,
        reason: reason || "init_failure",
      });
    } catch (e) { /* best-effort; the apology hint is optional */ }
  };

  // In-universe error screen — replaces sterile "The origin web server returned
  // an invalid or incomplete response" style copy with an atmosphere-tinted
  // "she stepped away" message. Fades ambience to silence over ~2.5s BEFORE
  // presenting the failure screen so the transition stays peaceful.
  // Auto-navigates Home after a beat.
  const triggerInUniverseError = (reason) => {
    if (endedRef.current) return;
    strace("SESSION", "in-universe error triggered", { reason });
    endedRef.current = true;
    setGlobalSessionEnded(true);
    fgService.stop();
    const key = params.atmosphereId || "default";
    const copy = STEPPED_AWAY_COPY[key] || STEPPED_AWAY_COPY.default;
    // Pick ONE explanation randomly — never concatenate multiple possibilities.
    const line = copy.lines[Math.floor(Math.random() * copy.lines.length)];
    const title = `${companionName} isn't here right now.`;
    setError({ title, lines: [line] });
    setPhase("error");
    // Immediately stop any speech/reactions so nothing plays over the failure copy.
    try { reactionAudioRef.current?.pause(); } catch (e) { /* ignore */ }
    try { audioRef.current?.pause(); } catch (e) { /* ignore */ }
    // Graceful ambient fade-out (2.5s) — no abrupt cutoff.
    if (mixerRef.current) {
      try { mixerRef.current.onEvent = null; } catch (e) { /* ignore */ }
      try { mixerRef.current.fadeToSilence(2.5); } catch (e) { /* ignore */ }
      setTimeout(() => {
        try { mixerRef.current?.stop(); } catch (e) { /* ignore */ }
        if (ambientAudioRef.current) {
          try { ambientAudioRef.current.pause(); ambientAudioRef.current.srcObject = null; } catch (e) { /* ignore */ }
        }
      }, 2700);
    } else if (ambientAudioRef.current) {
      try { ambientAudioRef.current.pause(); ambientAudioRef.current.srcObject = null; } catch (e) { /* ignore */ }
    }
    if (sessionTimerRef.current) { clearTimeout(sessionTimerRef.current); sessionTimerRef.current = null; }
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    if (guaranteedReactionTimerRef.current) { clearTimeout(guaranteedReactionTimerRef.current); guaranteedReactionTimerRef.current = null; }
    if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
    reportInitFailure(reason);
    // Return home once the ambience has fully faded and the copy has had a beat.
    setTimeout(() => { try { closeHostedSession(); nav("/app", { replace: true }); } catch (e) { /* ignore */ } }, 8500);
  };

  // WATCHDOG — if dialogue never actually starts playing within the budget,
  // treat this as a failed session initialization. Never let the user sit
  // through 15 minutes of ambience wondering where the companion is.
  // Iter 125 — STAGE-AWARE: re-armed at each pipeline milestone, with a fresh
  // recovery grace whenever the app returns to the foreground, so a delayed
  // timer can never kill a session that is mid-recovery after OS suspension.
  // Only ONE watchdog is ever active (each arm clears the previous handle).
  const armWatchdog = (ms, stage) => {
    if (watchdogRef.current) clearTimeout(watchdogRef.current);
    strace("WATCHDOG", `armed (${ms}ms) — stage: ${stage}`);
    watchdogRef.current = setTimeout(() => {
      if (dialogueStartedRef.current || endedRef.current || pausedRef.current) return;
      strace("WATCHDOG", `FIRED — dialogue never reached speakers (stage: ${stage})`);
      triggerInUniverseError("watchdog_timeout");
    }, ms);
  };

  useEffect(() => {
    // Generous window: LLM generate (~30-90s) + extension pass (~15-30s) +
    // TTS (~5-15s) + download + decode. 150s ceiling per stage.
    armWatchdog(150000, "init");
    const onVis = () => {
      if (
        document.visibilityState === "visible" &&
        !dialogueStartedRef.current && !endedRef.current && !pausedRef.current
      ) {
        strace("WATCHDOG", "app foregrounded during init — granting 75s recovery grace");
        armWatchdog(75000, "foreground_recovery");
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============= SESSION RUNTIME =============
  // The session IS the selected duration — not the speech. The companion may
  // speak, go quiet, react to the world, or stay silent. Only the timer ends it.

  const isSpeaking = () => {
    const a = audioRef.current;
    const r = reactionAudioRef.current;
    return (!!a && !a.paused && !a.ended) || (!!r && !r.paused && !r.ended);
  };

  const startSessionTimer = (ms) => {
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    sessionEndsAtRef.current = Date.now() + ms;
    sessionTimerRef.current = setTimeout(() => endSession(), ms);
  };

  const endSession = () => {
    if (endedRef.current) return;
    endedRef.current = true;
    setSessionEnded(true);
    setGlobalSessionEnded(true); // Iter 92 — morning auto-start may now fire past this session
    fgService.stop();
    setPlaying(false);
    if (sessionTimerRef.current) { clearTimeout(sessionTimerRef.current); sessionTimerRef.current = null; }
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    if (guaranteedReactionTimerRef.current) { clearTimeout(guaranteedReactionTimerRef.current); guaranteedReactionTimerRef.current = null; }
    // Iteration 58 — no more action SFX after the session ends.
    for (const t of actionCueTimersRef.current) { try { clearTimeout(t); } catch (e) { /* ignore */ } }
    actionCueTimersRef.current = [];
    // Iteration 59 — cancel any not-yet-fired pre-composed segment timers so
    // scheduled dialogue never plays after the session boundary. Also clear
    // the played-keys set so a replay session starts with a clean slate.
    for (const t of segmentTimersRef.current) { try { clearTimeout(t); } catch (e) { /* ignore */ } }
    segmentTimersRef.current = [];
    segmentPlayedRef.current = new Set();
    try { if (audioRef.current && !audioRef.current.ended) audioRef.current.pause(); } catch (e) { /* ignore */ }
    try { reactionAudioRef.current?.pause(); } catch (e) { /* ignore */ }
    // A real ending: the world drifts gently to silence rather than cutting off.
    if (mixerRef.current) mixerRef.current.fadeToSilence(25);
    try { if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused"; } catch (e) { /* ignore */ }
    // Phase 3 — kick off hybrid master rendering. This is fire-and-forget from
    // the client's perspective; SessionPlayer polls session.master_status on
    // replay. finalizeMaster() is fully self-guarded and never throws.
    finalizeMaster();
  };

  const finalizeMaster = async () => {
    if (finalizedRef.current) return;
    const sid = sessionIdRef.current;
    if (!sid) {
      strace("MASTER", "finalize SKIPPED — no session id yet");
      return;
    }
    finalizedRef.current = true;
    const durationSec = Math.max(30, Math.round((durationMsRef.current || 15 * 60 * 1000) / 1000));
    const payload = {
      duration_seconds: durationSec,
      dialogue_hash: dialogueAudioHashRef.current || null,
      dialogue_at_sec: dialogueAtSecRef.current || 0,
      ambient_loops: (ambientLoopUrlsRef.current || []).slice(0, 6),
      events: (timelineEventsRef.current || []).slice(0, 400),
    };
    strace("MASTER", "POST /sessions/{id}/finalize", {
      session_id: sid,
      duration_seconds: durationSec,
      dialogue_hash: payload.dialogue_hash,
      dialogue_at_sec: payload.dialogue_at_sec,
      ambient_loops_count: payload.ambient_loops.length,
      events_count: payload.events.length,
    });
    try {
      const { data } = await api.post(`/sessions/${sid}/finalize`, payload);
      strace("MASTER", "finalize accepted", {
        status: data?.status,
        ready: data?.ready,
        status_url: data?.status_url,
      });
    } catch (e) {
      strace("MASTER", "finalize FAILED (client)", {
        status: e?.response?.status,
        detail: e?.response?.data?.detail,
        message: e?.message,
      });
    }
  };

  // Personality-driven, probabilistic reaction — RARE. Ducks ambience, speaks a
  // short line, then returns to comfortable silence.
  const playReaction = (trigger) => {
    const bank = reactionBankRef.current;
    const entry = bank && bank[trigger];
    const items = (entry && entry.items) || [];
    if (!items.length) return;
    if (pausedRef.current || endedRef.current || isSpeaking()) return;
    const item = items[Math.floor(Math.random() * items.length)];
    if (!item || !item.url) return;
    reactionCooldownRef.current = Date.now() + 60000 + Math.random() * 60000; // 60–120s of calm after
    try {
      // TTS pipeline audit — bank lines were previously invisible at playback
      // (URL only, no text, no timeline event). Now every bank play is logged
      // and mirrored to the master timeline like any other spoken line.
      logAudioPlay(`reaction_bank:${trigger}`, item.text, { audio_hash: _hashFromAudioUrl(item.url) });
      if (mixerRef.current) mixerRef.current.duck(0.9);
      const a = new Audio(item.url);
      a.volume = DIALOGUE_VOLUME;
      reactionAudioRef.current = a;
      a.onended = () => { if (mixerRef.current && !pausedRef.current && !endedRef.current) mixerRef.current.unduck(1.0); };
      a.play().catch(() => { if (mixerRef.current) mixerRef.current.unduck(1.0); });
    } catch (e) { /* ignore */ }
  };

  // LIVE reactive dialogue — the LLM generates a fresh short line for THIS
  // moment (not from the canned pool) and we play its synthesized audio.
  // Also lets the LLM optionally trigger scenario/ambient events on the mixer
  // (e.g. "fire is fading" → she says something → 'fire_stir' plays).
  //
  // Iteration 57 — this is now the LEAF worker for the reactive queue.
  // Callers MUST route through enqueueReactive() so serialization + lock
  // ownership + [REACTIVE_TTS] tracing all live in one place.
  const requestLiveReaction = async (trigger, eventCategory) => {
    const sid = sessionIdRef.current;
    if (!sid) return false;
    if (pausedRef.current || endedRef.current) return false;
    // The lock/serialization is owned by the queue drain; this function
    // never runs concurrently with itself. reactiveInFlightRef is retained
    // for legacy callers but the queue drain is the real single-writer.
    if (reactiveInFlightRef.current) return false;
    reactiveInFlightRef.current = true;
    // Immediate cooldown so we never fire two live requests at once even if
    // more events land while the LLM is thinking (~2-6s).
    reactionCooldownRef.current = Date.now() + 45000;
    const _t0 = performance.now();
    rtrace("started", { trigger, event_category: eventCategory });
    try {
      const total = durationMsRef.current || 1;
      const remainingMs = Math.max(0, (sessionEndsAtRef.current || Date.now()) - Date.now());
      const elapsedMs = Math.max(0, total - remainingMs);
      const { data } = await api.post(`/sessions/${sid}/react`, {
        trigger,
        event_category: eventCategory || null,
        elapsed_seconds: Math.round(elapsedMs / 1000),
        remaining_seconds: Math.round(remainingMs / 1000),
      });
      if (data?.skipped) {
        rtrace("skipped", { reason: data?.reason || "backend_skipped", took_ms: Math.round(performance.now() - _t0) });
        return false;
      }
      if (pausedRef.current || endedRef.current) {
        rtrace("skipped", { reason: "session_ended_mid_generation", took_ms: Math.round(performance.now() - _t0) });
        return false;
      }
      const url = `${API}${data.audio_url.replace("/api", "")}`;
      // Capture the reactive line on the master timeline — the exact hash
      // lets the master renderer reuse the locally-cached MP3 without
      // re-downloading anything. TTS audit: text + source are recorded.
      logAudioPlay(`live_reaction:${trigger}`, data.dialogue || "", { audio_hash: data.audio_hash || null });
      if (mixerRef.current) mixerRef.current.duck(0.9);
      // Play the audio and RESOLVE the outer promise only when playback ends.
      // The queue drain awaits this promise so the next reactive line cannot
      // start until this one has fully finished playing.
      const playbackDone = new Promise((resolve) => {
        const a = new Audio(url);
        a.volume = DIALOGUE_VOLUME;
        reactionAudioRef.current = a;
        // Iter 75 — 350ms fade-out on reactive dialogue clips.
        try { attachDialogueFades(a); } catch (e) { /* ignore */ }
        const finish = (why) => {
          if (mixerRef.current && !pausedRef.current && !endedRef.current) mixerRef.current.unduck(1.0);
          // Fire any atmosphere updates the LLM invited AFTER she finishes
          // speaking — the world reacts to her, not on top of her voice.
          try {
            const updates = data.atmosphere_updates || {};
            (updates.scenario || []).forEach((cat) => {
              if (mixerRef.current) mixerRef.current.triggerScenarioEvent(cat);
            });
            const ae = updates.ambient_events || [];
            if (ae.length && mixerRef.current) mixerRef.current.scheduleEvents(ae);
          } catch (e) { /* ignore */ }
          rtrace("finished", {
            trigger, why,
            words_used: (data?.dialogue || "").trim().split(/\s+/).filter(Boolean).length,
            took_ms: Math.round(performance.now() - _t0),
          });
          resolve(true);
        };
        a.onended = () => finish("onended");
        a.onerror = () => finish("onerror");
        a.play().catch(() => finish("play_rejected"));
      });
      liveReactionCountRef.current += 1;
      // 60-140s of calm after a live line — the world may keep breathing
      // between her interactions, but she is not silent for the rest of the night.
      reactionCooldownRef.current = Date.now() + 60000 + Math.random() * 80000;
      await playbackDone;
      return true;
    } catch (e) {
      rtrace("failed", {
        trigger,
        status: e?.response?.status,
        message: e?.message || String(e),
        took_ms: Math.round(performance.now() - _t0),
      });
      // Silent fall-back — the canned bank keeps the session feeling alive.
      return false;
    } finally {
      reactiveInFlightRef.current = false;
    }
  };

  // ITERATION 57 — Reactive TTS QUEUE.
  //
  // Every path that wants the companion to speak a fresh live line routes
  // through here. The queue guarantees:
  //   • at most ONE ElevenLabs synthesis per session at a time
  //   • the initial monologue always wins (skip while ttsLock='initial')
  //   • no two reactive audios ever overlap in playback
  //   • stale queued events (>45s old) are dropped before pulling — the
  //     companion shouldn't react to thunder from 90s ago
  //   • probability + cooldown decisions still happen at the caller level,
  //     so the queue only holds events the caller actually wants to voice
  const enqueueReactive = (trigger, eventCategory) => {
    if (endedRef.current || pausedRef.current) {
      rtrace("skipped", { reason: "session_paused_or_ended", trigger });
      return;
    }
    // Iteration 59 — full-timeline mode owns EVERY beat; the reactive queue
    // stays completely silent so we never double up dialogue.
    if (hasFullTimelineRef.current) {
      rtrace("skipped", { reason: "full_timeline_mode", trigger });
      return;
    }
    if (ttsLockRef.current === "initial") {
      rtrace("skipped", { reason: "initial_pending", trigger });
      return;
    }
    if (reactiveQueueRef.current.length >= REACTIVE_QUEUE_MAX) {
      rtrace("skipped", { reason: "queue_full", trigger, queue_len: reactiveQueueRef.current.length });
      return;
    }
    const item = { trigger, event_category: eventCategory || null, enqueued_at: Date.now() };
    reactiveQueueRef.current.push(item);
    rtrace("queued", {
      trigger, event_category: eventCategory || null,
      queue_len: reactiveQueueRef.current.length,
      lock: ttsLockRef.current,
    });
    processReactiveQueue();
  };

  const processReactiveQueue = async () => {
    if (reactiveProcessingRef.current) return; // single-writer guarantee
    if (ttsLockRef.current === "initial") return;
    if (!reactiveQueueRef.current.length) return;
    reactiveProcessingRef.current = true;
    try {
      while (
        reactiveQueueRef.current.length &&
        !endedRef.current &&
        !pausedRef.current &&
        ttsLockRef.current !== "initial"
      ) {
        // Drop stale items at the head of the queue before we pull.
        while (
          reactiveQueueRef.current.length &&
          Date.now() - reactiveQueueRef.current[0].enqueued_at > REACTIVE_STALE_MS
        ) {
          const stale = reactiveQueueRef.current.shift();
          rtrace("skipped", { reason: "stale", trigger: stale.trigger, age_ms: Date.now() - stale.enqueued_at });
        }
        const item = reactiveQueueRef.current.shift();
        if (!item) break;
        // Never speak on top of the initial monologue or an already-playing
        // reactive line. If the main audio is currently talking, wait it out;
        // the drain will be reinvoked from onAudioEnded (initial) or from the
        // current line's onended (reactive).
        if (isSpeaking()) {
          rtrace("skipped", { reason: "isSpeaking_when_draining", trigger: item.trigger });
          continue;
        }
        ttsLockRef.current = "reactive";
        try {
          await requestLiveReaction(item.trigger, item.event_category);
        } finally {
          ttsLockRef.current = null;
        }
      }
    } finally {
      reactiveProcessingRef.current = false;
    }
  };

  // GUARANTEED PRESENCE — even if no world event fires and no idle line lands
  // through pure probability, the companion MUST have at least one live-generated
  // mid-session interaction. This scheduler waits until we're ~60% through the
  // session; if no live reactive has played yet, it forces one softly.
  const scheduleGuaranteedReaction = () => {
    if (guaranteedReactionTimerRef.current) clearTimeout(guaranteedReactionTimerRef.current);
    const total = durationMsRef.current || 15 * 60 * 1000;
    const delay = Math.max(30000, Math.floor(total * 0.6));
    guaranteedReactionTimerRef.current = setTimeout(async function attemptGuaranteed() {
      if (pausedRef.current || endedRef.current) return;
      if (liveReactionCountRef.current > 0) return; // already had one, good
      // If the pipeline is still locked to the initial monologue OR another
      // reactive is being generated/played, wait it out. The drain loop will
      // resume automatically when playback ends.
      if (isSpeaking() || ttsLockRef.current !== null || reactiveQueueRef.current.length) {
        guaranteedReactionTimerRef.current = setTimeout(attemptGuaranteed, 20000);
        return;
      }
      // Clear cooldown so this one is allowed through — this is the safety net.
      reactionCooldownRef.current = 0;
      enqueueReactive("silence", null);
      // Give the queue a moment; if it never processed (all skips), retry later.
      setTimeout(() => {
        if (liveReactionCountRef.current === 0 && !endedRef.current && !pausedRef.current) {
          playReaction("idle");
          guaranteedReactionTimerRef.current = setTimeout(attemptGuaranteed, 150000);
        }
      }, 8000);
    }, delay);
  };

  const maybeReact = (trigger, eventCategory) => {
    const entry = reactionBankRef.current && reactionBankRef.current[trigger];
    if (!entry) return;
    if (Math.random() > entry.p * reactionBiasRef.current) return; // personality + mood/need weight → most events pass silently
    // Some proportion of reactions escalate to a LIVE LLM-generated line
    // (fresh for THIS moment); the rest fall back to the canned pool so we
    // never overspend the reactive word budget.
    if (Math.random() < liveReactionProbabilityRef.current) {
      // Enqueue rather than fire — the queue guarantees single-flight TTS +
      // "initial monologue always wins" priority. If the LLM/TTS ends up
      // producing nothing (skipped/failed), the canned pool never plays here;
      // we accept the silence as a natural gap rather than double-speaking.
      enqueueReactive(trigger, eventCategory);
    } else {
      playReaction(trigger);
    }
  };

  // Fired by the mixer when a world event ACTUALLY plays (never before it's heard).
  const handleWorldEvent = (category, meta) => {
    // Capture the event on the master timeline BEFORE any speaking gates —
    // we want a faithful replay of what the user actually heard, even if the
    // companion chose to stay silent through it.
    try {
      timelineEventsRef.current.push({
        type: "ambient_event",
        at_sec: _now_at_sec(),
        category: category || null,
        url: meta?.url || null,
      });
    } catch (e) { /* ignore */ }
    if (pausedRef.current || endedRef.current || isSpeaking()) return;
    if (Date.now() < reactionCooldownRef.current) return;
    const trigger = triggerForCategory(category);
    if (!trigger) return;
    maybeReact(trigger, category);
  };

  // Occasional soft presence during long silences (personality "idle" lines),
  // spread EVENLY across the whole session with a gentle taper toward sleep so
  // the world stays alive from start to end while calming as sleep approaches.
  const scheduleIdle = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    const wait = 70000 + Math.random() * 70000; // 70–140s
    idleTimerRef.current = setTimeout(() => {
      if (!pausedRef.current && !endedRef.current && !isSpeaking() && Date.now() >= reactionCooldownRef.current) {
        const total = durationMsRef.current || 1;
        const remaining = Math.max(0, (sessionEndsAtRef.current || Date.now()) - Date.now());
        const progress = Math.min(1, Math.max(0, (total - remaining) / total));
        // Full presence through the first 70%; ease down to ~30% by the end.
        const taper = progress < 0.7 ? 1 : Math.max(0.3, 1 - (progress - 0.7) / 0.3 * 0.7);
        if (Math.random() < 0.34 * taper) maybeReact("idle");
      }
      if (!pausedRef.current && !endedRef.current) scheduleIdle();
    }, wait);
  };

  // Replace the canned reaction pool with LLM-authored, in-character lines for
  // tonight (idle + world-event triggers), keeping the tuned probabilities.
  const mergePresenceLines = async (lines) => {
    if (!lines || typeof lines !== "object") return;
    const DEFAULT_P = { idle: 0.16, thunder: 0.5, rain: 0.25, fire: 0.2, owl: 0.2, ocean: 0.2 };
    const existing = reactionBankRef.current || {};
    const built = { ...existing };
    await Promise.all(Object.entries(lines).map(async ([trigger, arr]) => {
      if (!Array.isArray(arr) || !arr.length) return;
      const items = [];
      for (const text of arr.slice(0, trigger === "idle" ? 6 : 3)) {
        try {
          const r = await api.post("/tts/synthesize", {
            character_id: params.characterId,
            text,
            source: `presence_line:${trigger}`,
            session_id: sessionIdRef.current || null,
          });
          items.push({ url: `${API}${r.data.url.replace("/api", "")}`, text });
        } catch (e) { /* skip line gracefully */ }
      }
      if (items.length) {
        const p = existing[trigger]?.p ?? DEFAULT_P[trigger] ?? 0.2;
        built[trigger] = { p, items };
      }
    }));
    reactionBankRef.current = built;
  };

  const setupReactions = async () => {
    if (reactionsReadyRef.current) return;
    reactionsReadyRef.current = true;
    const bank = bankForCharacter(params.characterId);
    const built = {};
    await Promise.all(Object.entries(bank).map(async ([trigger, cfg]) => {
      const items = [];
      for (const text of (cfg.lines || []).slice(0, 2)) { // cap pre-render cost
        try {
          const r = await api.post("/tts/synthesize", {
            character_id: params.characterId,
            text,
            source: `canned_bank:${trigger}`,
            session_id: sessionIdRef.current || null,
          });
          items.push({ url: `${API}${r.data.url.replace("/api", "")}`, text });
        } catch (e) { /* skip this line gracefully — partial bank is fine */ }
      }
      built[trigger] = { p: cfg.p, items };
    }));
    reactionBankRef.current = built;
  };

  const pauseSession = () => {
    pausedRef.current = true;
    setPlaying(false);
    try { if (audioRef.current && !audioRef.current.ended && !audioRef.current.paused) audioRef.current.pause(); } catch (e) { /* ignore */ }
    try { reactionAudioRef.current?.pause(); } catch (e) { /* ignore */ }
    if (mixerRef.current) mixerRef.current.pause();
    if (ambientAudioRef.current) { try { ambientAudioRef.current.pause(); } catch (e) { /* ignore */ } }
    if (sessionTimerRef.current) {
      remainingMsRef.current = Math.max(0, (sessionEndsAtRef.current || Date.now()) - Date.now());
      clearTimeout(sessionTimerRef.current); sessionTimerRef.current = null;
    }
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    // Iteration 58 — cancel pending action SFX timers on pause. On resume,
    // scheduleActionCues() reschedules them from the current audio playhead
    // so the action beats still land in the right place.
    clearActionCueTimers();
    // Iteration 59 — cancel pending pre-composed segment timers on pause.
    // resumeSession() will re-run scheduleTimelineSegments() from the current
    // session elapsed time so any not-yet-played segments still land.
    clearSegmentTimers();
    // Iteration 57 — drop any queued reactive lines on pause. When she comes
    // back to the session, the world state is stale; better to react to what
    // is happening THEN than to catch up on 5-minute-old owls.
    if (reactiveQueueRef.current.length) {
      const dropped = reactiveQueueRef.current.length;
      reactiveQueueRef.current = [];
      rtrace("skipped", { reason: "queue_cleared_on_pause", dropped });
    }
    try { if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused"; } catch (e) { /* ignore */ }
  };

  const resumeSession = () => {
    if (endedRef.current) return;
    pausedRef.current = false;
    setPlaying(true);
    if (mixerRef.current) mixerRef.current.resume();
    if (ambientAudioRef.current) { ambientAudioRef.current.play().catch(() => { /* ignore */ }); }
    try { if (audioRef.current && !audioRef.current.ended) audioRef.current.play().catch(() => { /* ignore */ }); } catch (e) { /* ignore */ }
    try { const r = reactionAudioRef.current; if (r && r.paused && !r.ended) r.play().catch(() => { /* ignore */ }); } catch (e) { /* ignore */ }
    if (remainingMsRef.current != null) startSessionTimer(remainingMsRef.current);
    scheduleIdle();
    // Iteration 58 — reschedule remaining action cues from the current
    // playhead so paused-then-resumed dialogue keeps its action beats.
    scheduleActionCues();
    // Iteration 59 — reschedule any not-yet-played timeline segments too.
    if (hasFullTimelineRef.current) scheduleTimelineSegments();
    // Iter 82 — if the opening cluster chain was waiting out a silence gap
    // when the session paused, re-arm it (the gap timer was cleared with the
    // segment timers).
    if ((openingChainRef.current || []).length &&
        openingChainStateRef.current === "waiting" &&
        openingChainIndexRef.current < openingChainRef.current.length) {
      playNextOpeningCluster();
    }
    // Iteration 57 — nothing to drain right now (queue was cleared on pause)
    // but rearm the processor guard so future events can flow again.
    if (ttsLockRef.current !== "initial") processReactiveQueue();
    try { if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing"; } catch (e) { /* ignore */ }
  };

  // ============= STEP 1 — ambient within ~100ms =============
  useEffect(() => {
    if (!params.mode) {
      strace("SESSION", "no mode in route state — bouncing to /app");
      closeHostedSession();
      nav("/app", { replace: true });
      return undefined;
    }
    if (triedAmbientRef.current) return undefined;
    triedAmbientRef.current = true;
    setGlobalSessionEnded(false); // Iter 92 — a fresh hosted session is live again
    strace("SESSION", "created", {
      mode: params.mode,
      duration_minutes: params.durationMinutes,
      characterId: params.characterId,
      atmosphereId: params.atmosphereId,
    });

    (async () => {
      // Resolve the character label up front (purely for the screen, not gating)
      api.get("/catalog/characters")
        .then((r) => {
          const c = r.data.find((x) => x.id === params.characterId);
          if (c) setCharacter(c);
        })
        .catch(() => {});

      // Start ambient as the very first thing. No waiting.
      try {
        strace("AMBIENT", "fetching audio_pack", { atmosphereId: params.atmosphereId, morning: isMorning });
        const packQuery = params.characterId ? { character_id: params.characterId } : {};
        let pack = await api.get(
          `/atmosphere/${params.atmosphereId || "custom"}/audio_pack`,
          { params: packQuery }
        );
        // Iter 89 — resilience: an empty loop pack means silent atmosphere for
        // the whole night. Retry once (backend discovery may have been cold).
        if (!(pack.data.sound_library?.ambient_loops?.assets || []).length) {
          strace("AMBIENT", "audio_pack had 0 loop assets — retrying once");
          await new Promise((r) => setTimeout(r, 1200));
          pack = await api.get(
            `/atmosphere/${params.atmosphereId || "custom"}/audio_pack`,
            { params: packQuery }
          );
        }
        // Iter 92 — morning sessions START in the lingering NIGHT world and
        // crossfade into the morning world ~1 min in (owls hand over to birds).
        let morningPack = null;
        if (isMorning) {
          try {
            morningPack = (await api.get(
              `/atmosphere/${params.atmosphereId || "custom"}/audio_pack`,
              { params: { ...packQuery, variant: "morning" } }
            )).data;
            strace("MORNING", "morning variant pack fetched", {
              loops: morningPack.sound_library?.ambient_loops?.assets?.length || 0,
              events: morningPack.sound_library?.ambient_events?.assets?.length || 0,
            });
          } catch (e) {
            strace("MORNING", "morning pack fetch FAILED — staying on night pack", { error: String(e?.message || e) });
          }
        }
        strace("AMBIENT", "audio_pack received", {
          env_synth: pack.data.environment?.synth,
          ambient_loops_count: pack.data.sound_library?.ambient_loops?.assets?.length || 0,
          ambient_events_count: pack.data.sound_library?.ambient_events?.assets?.length || 0,
          presence_present: !!pack.data.presence,
          character_scenario_count: pack.data.character_scenario?.length || 0,
        });
        // Phase 3 — remember which ambient loops back this session so the
        // master renderer can tile the exact same beds beneath dialogue.
        try {
          const loops = ((isMorning && morningPack)
            ? morningPack.sound_library?.ambient_loops?.assets
            : pack.data.sound_library?.ambient_loops?.assets) || [];
          ambientLoopUrlsRef.current = loops
            .filter((a) => a?.storage?.url)
            .map((a) => ({
              url: a.storage.url,
              name: a.name || null,
              category: a.category || null,
            }));
          strace("MASTER", "ambient loops registered for master track", {
            count: ambientLoopUrlsRef.current.length,
          });
        } catch (e) { /* ignore — timeline just won't include ambient bed */ }
        const mixer = new AmbientMixer();
        mixerRef.current = mixer;
        await mixer.start({
          environment: {
            synth: pack.data.environment.synth,
            environment_state: pack.data.environment.environment_state,
          },
          soundPlan: pack.data.sound_library,
          environmentState: pack.data.environment.environment_state,
          fadeInSec: isMorning ? 5 : undefined, // Iter 91 — the morning world returns slowly
        });
        strace("AMBIENT", "mixer.start() completed");
        // Anchor the session clock at ambient start (t=0 of the session).
        if (sessionStartMsRef.current == null) {
          sessionStartMsRef.current = (typeof performance !== "undefined") ? performance.now() : Date.now();
        }
        // Iter 87 — Presence Events v1: shared ambient presence one-shots
        // (blanket rustle / mattress shift). Sleep mode only; the backend
        // only ships them for Cozy Cabin. Sparse, randomized, never edges.
        if (params.mode === "sleep" && pack.data.ambient_presence?.events?.length) {
          const _presSched = mixer.scheduleSharedPresenceEvents(
            pack.data.ambient_presence,
            (params.durationMinutes || 15) * 60,
          );
          strace("PRESENCE", "shared ambient presence events scheduled", {
            count: _presSched.length,
            schedule: _presSched,
          });
        }
        // Iter 103b — breathing presence bed (no-op until breathing SFX are
        // uploaded to the sound library; auto-activates when they exist).
        if (params.mode === "sleep" && pack.data.ambient_presence?.breathing?.length) {
          const _bCount = mixer.scheduleBreathingBed(
            pack.data.ambient_presence.breathing,
            (params.durationMinutes || 15) * 60,
          );
          strace("PRESENCE", "breathing bed scheduled", { count: _bCount });
        }
        // Iter 69 — silent audio keep-alive: keeps the browser's media session
        // active continuously so WebAudio survives lock-screen / backgrounded
        // states (see /app/frontend/src/lib/silentKeepAlive.js).
        try { startKeepAlive(); } catch (e) { /* ignore */ }
        // Android: foreground media service keeps the session alive overnight
        // (no-op on web/iOS). Was referenced but never imported/started after
        // the Iter 96 refactor — the stop calls crashed on unmount cleanup.
        fgService.start();
        // Mirror the synthesized ambient into a hidden <audio> element so iOS
        // Safari / Android Chrome keep it playing under the lock screen.
        if (ambientAudioRef.current) {
          attachAmbientStreamToAudio(mixer, ambientAudioRef.current);
          strace("AMBIENT", "hidden <audio> mirror attached");
        }
        // Run random environmental events from default tags right away —
        // these are replaced with the LLM-emitted tags once generation completes.
        if (!pack.data.sound_library?.ambient_events?.assets?.length) {
          const defaultTags = pack.data.environment.default_tags || [];
          mixer.scheduleEvents(defaultTags);
        }
        // Cache presence/scenario packs so we can start them AFTER dialogue
        // actually begins. The companion cannot physically "exist" inside the
        // session if she never entered it — no breathing / hissing /
        // slithering / heartbeat may fire before dialogue playback starts.
        pendingPresenceRef.current = pack.data.presence || null;
        pendingScenarioRef.current = pack.data.character_scenario || null;
        // Stash the heartbeat asset — played only later, and only if the scene
        // establishes a close embrace (see onAudioEnded).
        heartbeatUrlRef.current = pack.data.presence?.heartbeat?.[0]?.url || null;
        // Iter 108 — hand the persistent-presence assets to the mixer so the
        // cuddle state can start heartbeat + breathing loops on its own.
        mixer.setPresenceAssets({
          heartbeatUrl: heartbeatUrlRef.current,
          breathingLoops: pack.data.presence?.loops || [],
          passiveCoiling: pack.data.ambient_presence?.passive_coiling || [],
        });
        mixer.onCuddleEstablished = () => {
          strace("PRESENCE", "cuddle established — persistent presence begins (heartbeat + breathing)");
          if (sessionIdRef.current) {
            api.post(`/sessions/${sessionIdRef.current}/presence-state`, { cuddled: true }).catch(() => { /* never blocks playback */ });
          }
        };
        strace("PRESENCE", "packs cached — WILL be armed after [PLAYER] onPlay");
        if (isMorning) {
          // Iter 92 — PRESENCE-FIRST OPENING: she is physically there before
          // she speaks. Breathing / blanket / heartbeat surface under the
          // quiet lingering night ambience while the listener half-wakes.
          presenceStartedRef.current = true;
          try {
            if (pendingPresenceRef.current) mixer.schedulePresenceEvents(pendingPresenceRef.current);
            if (heartbeatUrlRef.current) mixer.startHeartbeat(heartbeatUrlRef.current, 0.4);
            // Iter 108 — presence continuity across sleep: the night ended in
            // her embrace, so the morning WAKES inside it — the breathing loop
            // is already going before the morning world fades in.
            const _bl = pendingPresenceRef.current?.loops || [];
            if (_bl.length) mixer.startBreathingBed(_bl);
            strace("MORNING", "presence-first opening armed (presence + heartbeat + breathing before dialogue)");
          } catch (e) { /* ignore */ }
          if (morningPack) {
            const _mLoops = morningPack.sound_library?.ambient_loops?.assets || [];
            const _mEvents = morningPack.sound_library?.ambient_events;
            const _t = setTimeout(() => {
              if (!mixerRef.current || endedRef.current) return;
              try {
                mixerRef.current.crossfadeToLoops(_mLoops, 10);
                if (_mEvents) mixerRef.current.scheduleAmbientEvents(_mEvents);
                strace("MORNING", "night → morning ambience crossfade started", { morning_loops: _mLoops.length });
              } catch (e) { /* ignore */ }
            }, MORNING_CROSSFADE_AT_MS);
            morningTimersRef.current.push(_t);
          }
          // Iter 94 — safety net: if no "moving away" line ever fires, the
          // embrace still ends around 70% of the session (never at the start).
          const _rel = setTimeout(
            () => releaseMorningEmbrace("fallback_70pct"),
            Math.max(120000, (durationMsRef.current || 15 * 60 * 1000) * 0.7)
          );
          morningTimersRef.current.push(_rel);
        }
      } catch (e) {
        strace("AMBIENT", "FAILED to start", { error: String(e?.message || e) });
        console.warn("arrival: ambient failed to start", e);
      }

      // The session lives for the FULL selected duration — not until speech ends.
      startSessionTimer(durationMsRef.current);
      if (mixerRef.current) {
        mixerRef.current.onEvent = (category, meta) => handleWorldEvent(category, meta);
        setupReactions();
      }
      // Begin the gentle "she's still here" presence loop immediately, spread
      // evenly across the whole session so it never front-loads or goes silent.
      scheduleIdle();
      // Safety-net: guarantee at least one mid-session live reactive line.
      scheduleGuaranteedReaction();
    })();

    return undefined;
  }, []);

  // ============= STEP 2 — generate in background =============
  // Iter 87 — RESUME: reattach the UI to an already-generated session that is
  // still inside its duration window (after a refresh / app restart). No new
  // generation, no replay of the opening — ambient + remaining timeline only.
  const attachToExistingSession = async (resumeId) => {
    try {
      strace("SESSION", "RESUME — attaching to existing session", { session_id: resumeId });
      const { data: s } = await api.get(`/sessions/${resumeId}`);
      if (!s || s.status === "failed") { triggerInUniverseError("resume_not_found"); return; }
      const createdMs = new Date(s.created_at).getTime();
      const elapsedMs = Math.max(0, Date.now() - createdMs);
      const elapsedSec = elapsedMs / 1000;
      sessionIdRef.current = s.id;
      setMemoryTitle(s.memory_title || null);
      try {
        mixerRef.current?.setPresenceProfile({
          highIntimacy: !!s.night_scenario?.hair_touch_allowed,
          compositeChance: s.night_scenario?.presence_intensity === "deep" ? 0.75 : 0.5,
        });
      } catch (e) { /* ignore */ }
      if (s.presence_lines) mergePresenceLines(s.presence_lines);
      // Backdate the session clock so the at_sec scheduler fires only future segments.
      sessionStartMsRef.current = ((typeof performance !== "undefined") ? performance.now() : Date.now()) - elapsedMs;
      const timeline = Array.isArray(s.dialogue_timeline) ? s.dialogue_timeline : [];
      dialogueTimelineRef.current = timeline;
      hasFullTimelineRef.current = timeline.some((x) => x.audio_hash);
      // MAIN-TIMELINE PLAYBACK FIX — the old resume path ALWAYS assumed "she
      // already spoke tonight" and discarded every opening segment. If the app
      // reloaded/reattached BEFORE the opening ever reached the speakers
      // (locked screen during generation, webview suspension), the entire
      // authored opening was silently lost and only presence murmurs played.
      // The backend records playback_confirmed the moment dialogue actually
      // starts; resume now trusts that flag instead of assuming.
      const openingSegs = timeline
        .filter((seg) => String(seg.kind || "").startsWith("opening") && seg.audio_url)
        .sort((a, b) => (a.cluster_index || 0) - (b.cluster_index || 0));
      const openingAlreadySpoken = !!s.playback_confirmed || !openingSegs.length;
      timeline.forEach((seg, i) => {
        const key = `${i}:${seg.at_sec}:${seg.audio_hash || ""}`;
        if (String(seg.kind || "").startsWith("opening") || (seg.at_sec || 0) <= elapsedSec) {
          segmentPlayedRef.current.add(key);
        }
      });
      if (openingAlreadySpoken) {
        openingChainRef.current = []; // the opening never replays once confirmed spoken
        dialogueStartedRef.current = true; // she already spoke tonight
        strace("SESSION", "RESUME — opening confirmed spoken, not replayed", {
          playback_confirmed: !!s.playback_confirmed,
        });
      } else {
        // Replay the full opening via the chain player — it never reached the
        // speakers before this reattach. dialogueStartedRef stays false until
        // real audible playback (set inside the chain player's play() success).
        openingChainRef.current = openingSegs;
        openingChainIndexRef.current = 0;
        openingChainStateRef.current = "idle";
        strace("SESSION", "RESUME — opening was NEVER played; replaying opening chain", {
          clusters: openingSegs.length,
          elapsed_sec: Math.round(elapsedSec),
        });
        // SILENT-FAILURE DETECTION — the replayed opening must actually reach
        // the speakers; if it cannot within 45s (rejected play with no gesture),
        // the session enters the detectable in-universe failure state instead
        // of continuing silently forever.
        armWatchdog(45000, "resume_opening_replay");
      }
      ttsLockRef.current = null;
      // Watchdog stays ARMED when the opening is being replayed (silent-failure
      // detection); cleared only when she already spoke tonight.
      if (openingAlreadySpoken && watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
      // Presence loops normally arm on first dialogue play — arm them now
      // (retry once in case the ambient pack is still loading in STEP 1).
      const armPresence = () => {
        if (mixerRef.current && pendingPresenceRef.current && !presenceStartedRef.current) {
          presenceStartedRef.current = true;
          try { mixerRef.current.schedulePresenceEvents(pendingPresenceRef.current); } catch (e) { /* ignore */ }
        }
      };
      armPresence();
      setTimeout(armPresence, 6000);
      scheduleTimelineSegments();
      if (openingChainRef.current.length && !dialogueStartedRef.current) {
        playNextOpeningCluster();
      }
      setPhase("playing");
      try { window.history.replaceState(null, "", `/sessions/${s.id}`); } catch (e) { /* ignore */ }
      strace("SESSION", "RESUME attached", { elapsed_sec: Math.round(elapsedSec), segments: timeline.length });
    } catch (e) {
      strace("SESSION", "RESUME failed", { error: String(e?.message || e) });
      triggerInUniverseError("resume_failed");
    }
  };

  // Iter 94 — the transition from intimate closeness to normal morning
  // conversation: heartbeat fades ONLY once she naturally moves away in the
  // dialogue (fallback: ~70% of the session, in case no line ever matches).
  const releaseMorningEmbrace = (why) => {
    if (!isMorning || morningEmbraceReleasedRef.current) return;
    morningEmbraceReleasedRef.current = true;
    try { mixerRef.current?.stopHeartbeat(); } catch (e) { /* ignore */ }
    try { mixerRef.current?.stopBreathingLoop(); } catch (e) { /* ignore */ }
    strace("MORNING", "embrace released — heartbeat + breathing fading out", { why });
  };

  // Iter 92 — the morning opening waits until ~65s of session time so the
  // presence-first minute (night ambience + breathing/heartbeat + crossfade
  // into the morning world) plays out before she says a word.
  const waitForMorningOpeningGate = async () => {
    if (!isMorning) return;
    const nowMs = (typeof performance !== "undefined") ? performance.now() : Date.now();
    const elapsed = sessionStartMsRef.current != null ? nowMs - sessionStartMsRef.current : MORNING_OPENING_GATE_MS;
    const waitMs = MORNING_OPENING_GATE_MS - elapsed;
    if (waitMs <= 0 || endedRef.current) return;
    strace("MORNING", "presence-first gate — delaying opening dialogue", { wait_ms: Math.round(waitMs) });
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = setTimeout(() => {
        if (!dialogueStartedRef.current && !endedRef.current) triggerInUniverseError("watchdog_timeout");
      }, waitMs + 90000);
    }
    await new Promise((r) => setTimeout(r, waitMs));
  };

  useEffect(() => {
    if (triedGenRef.current) return undefined;
    triedGenRef.current = true;

    (async () => {
      if (params.resumeSessionId) {
        await attachToExistingSession(params.resumeSessionId);
        return;
      }
      try {
        const payload = {
          mode: params.mode,
          generate_morning: !!params.generateMorning,
          duration_minutes: params.durationMinutes || 15,
        };
        if (params.kind === "morning") {
          payload.kind = "morning";
          if (params.wakeTime) payload.wake_time = params.wakeTime;
        }
        if (params.atmosphereId && params.atmosphereId !== "custom") {
          payload.atmosphere_id = params.atmosphereId;
        }
        if (params.atmosphereId === "custom" && params.customAtmosphere) {
          payload.custom_atmosphere = params.customAtmosphere;
        }
        strace("LLM", "POST /sessions/generate → starting");
        const t0 = performance.now();
        // Stabilization pass — ROOT CAUSE of the "works only on the second
        // attempt" failure: this POST previously had NO timeout and NO retry
        // (the iter-125 helpers existed but were never wired to this call
        // site). On a clean app launch the very first request rides a cold
        // radio/TLS path; one hang or lost response silently killed the
        // night until the user cancelled and tried again. Now: bounded
        // timeout + retries (idempotent server-side via client_request_id),
        // and if the POST landed but its response was lost, the session is
        // adopted instead of abandoned.
        const clientRequestId = (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `crq_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        payload.client_request_id = clientRequestId;
        let data;
        try {
          ({ data } = await postWithRetry("/sessions/generate", payload, {
            label: "generate",
            trace: (msg, extra) => strace("LLM", msg, extra),
          }));
        } catch (postErr) {
          strace("LLM", "generate POST failed after retries — checking for an orphan session", {
            status: postErr?.response?.status,
            message: postErr?.message,
          });
          const orphan = await findOrphanSession(clientRequestId);
          if (!orphan) throw postErr;
          const { data: fullDoc } = await api.get(`/sessions/${orphan.id}`, { timeout: 15000 });
          data = { evening: fullDoc, morning: null, morning_pending: false, poll_interval_ms: 2000 };
          strace("LLM", "orphan session ADOPTED — the POST landed, its response was lost", {
            session_id: orphan.id,
            status: fullDoc?.status,
          });
        }
        const dtMs = Math.round(performance.now() - t0);
        strace("LLM", "placeholder response received", {
          took_ms: dtMs,
          session_id: data.evening?.id,
          status: data.evening?.status,
          poll_interval_ms: data.poll_interval_ms,
        });

        // iter55 — /sessions/generate is now fire-and-poll: it returns a
        // placeholder session doc immediately (status='generating') and does
        // the LLM work in the background. Poll GET /sessions/{id} until the
        // status flips to 'ready' or 'failed'. This decouples LLM latency
        // (30-80s) from Cloudflare's ~100s ingress budget — a <2s HTTP
        // response can never 502.
        if (data.evening?.status === "generating") {
          const sid = data.evening.id;
          sessionIdRef.current = sid; // recovery paths may need it before 'ready'
          armWatchdog(200000, "placeholder_received");
          const pollInterval = data.poll_interval_ms || 2000;
          // Iter 125 — suspension-aware budget: locked/backgrounded time does
          // NOT count against the deadline. iOS freezes JS while suspended;
          // the session keeps generating server-side the whole time, so a
          // wall-clock deadline here silently killed perfectly good sessions.
          const genClock = makeActiveClock(180000);
          while (genClock.tick()) {
            await new Promise((res) => setTimeout(res, pollInterval));
            if (endedRef.current) return;
            try {
              const st = await api.get(`/sessions/${sid}`);
              if (st.data?.status === "ready" && st.data?.dialogue) {
                strace("LLM", "BG generation READY", {
                  poll_elapsed_ms: Math.round(performance.now() - t0),
                });
                armWatchdog(120000, "generation_ready");
                data = { evening: st.data, morning: null, morning_pending: false };
                break;
              }
              if (st.data?.status === "failed") {
                strace("LLM", "BG generation FAILED", { error: st.data.error });
                triggerInUniverseError("generate_bg_failed");
                return;
              }
              // still generating → keep polling
            } catch (pollErr) {
              strace("LLM", "poll error (transient)", {
                status: pollErr?.response?.status,
                message: pollErr?.message,
              });
            }
          }
          if (data.evening?.status !== "ready" || !data.evening?.dialogue) {
            strace("LLM", "BG generation POLL_TIMEOUT (never became ready within 180s of ACTIVE time)");
            triggerInUniverseError("generate_poll_timeout");
            return;
          }
        }

        const plainText = (data.evening?.dialogue || "").replace(/\[[^\]]+\]/g, "");
        const words = plainText.trim().split(/\s+/).filter(Boolean).length;
        strace("LLM", "completed", {
          total_took_ms: Math.round(performance.now() - t0),
          session_id: data.evening?.id,
          dialogue_chars: (data.evening?.dialogue || "").length,
          dialogue_words: words,
          has_atmosphere_meta: !!data.evening?.atmosphere_meta,
          has_premise: !!data.evening?.premise,
          presence_lines_keys: Object.keys(data.evening?.presence_lines || {}),
          heartbeat: !!data.evening?.heartbeat,
        });
        // Iter 100 — high-intimacy presence allowance (hair touch) comes from
        // the session's Night Scenario; composites pick lazily at fire time.
        try {
          const _ns = data.evening?.night_scenario || {};
          mixerRef.current?.setPresenceProfile({
            highIntimacy: !!_ns.hair_touch_allowed,
            compositeChance: _ns.presence_intensity === "deep" ? 0.75 : 0.5,
          });
        } catch (e) { /* ignore */ }
        if (!data.evening?.dialogue || words < 20) {
          strace("LLM", "WARNING — dialogue looks empty or absurdly short", { words });
        }

        const sid = data.evening.id;
        sessionIdRef.current = sid;
        setMemoryTitle(data.evening.memory_title || null);
        // Mood + need shape HOW OFTEN she reacts to the world tonight (an anxious
        // storm-night reacts more; a sleepy "needs silence" night almost never).
        if (typeof data.evening.reaction_bias === "number") {
          reactionBiasRef.current = Math.max(0.2, Math.min(2, data.evening.reaction_bias));
        }
        // Longer sessions have more reactive-word budget → we lean more on
        // LIVE LLM-generated lines (fresh for the moment) vs the canned pool.
        // Short sessions stay closer to the canned bank so budget can't run out.
        const dm = params.durationMinutes || 15;
        if (dm <= 15) liveReactionProbabilityRef.current = 0.35;
        else if (dm <= 30) liveReactionProbabilityRef.current = 0.55;
        else if (dm <= 60) liveReactionProbabilityRef.current = 0.7;
        else liveReactionProbabilityRef.current = 0.8;
        // Upgrade the reaction/idle pool with LLM-authored, in-character lines
        // for tonight — she keeps speaking naturally throughout, never narrating.
        if (data.evening.presence_lines) mergePresenceLines(data.evening.presence_lines);
        // Only enable heartbeat if tonight's scene is a close chest-resting embrace.
        heartbeatEnabledRef.current = !!data.evening.heartbeat;
        // Iter 108 — the previous night ended in an established embrace.
        if (data.evening.presence_carryover?.cuddled) {
          strace("PRESENCE", "carryover — last night ended cuddled; waking inside the embrace");
        }
        // Iteration 58 — remember action cues; scheduling waits until we have
        // the true dialogue audio duration (loadedmetadata) because timing is
        // interpolated from text_offset_ratio.
        if (Array.isArray(data.evening.action_cues) && data.evening.action_cues.length) {
          actionCuesRef.current = data.evening.action_cues;
          strace("ACTION_DIRECTOR", "cues received", {
            count: actionCuesRef.current.length,
            categories: actionCuesRef.current.map((c) => c.category),
          });
        }
        // Iteration 59 — if the backend supplied a pre-composed dialogue
        // timeline with rendered per-segment audio URLs, we short-circuit the
        // legacy single-MP3 + runtime reactive path and switch to
        // deterministic-scheduler mode. Any segment without audio_hash falls
        // back through the normal render pipeline (best-effort).
        let timeline = Array.isArray(data.evening.dialogue_timeline) ? data.evening.dialogue_timeline : null;
        // Iter 82 fix — the session flips to status 'ready' the moment the
        // LLM finishes, but segment audio is patched in by a background
        // pre-render worker that starts AFTER that. Opening clusters render
        // FIRST (~5-20s). If the timeline exists but the opening carries no
        // audio yet, wait briefly for it instead of silently falling back to
        // the legacy single-MP3 path (which made the opening-chain feature
        // unreachable end-to-end).
        const _openingOf = (tl) => (tl || []).find((s) => s.kind === "opening") || (tl || [])[0];
        if (timeline && timeline.length && !_openingOf(timeline)?.audio_hash) {
          strace("FULL_TIMELINE", "timeline present but opening audio not rendered yet — waiting for pre-render", {
            segment_count: timeline.length,
          });
          // Extend the watchdog while we wait (same pattern as the autoplay gate).
          armWatchdog(90000, "timeline_prerender_wait");
          const _waitStart = Date.now();
          const _tlClock = makeActiveClock(40000); // Iter 125 — suspension-aware
          while (_tlClock.tick() && !endedRef.current) {
            await new Promise((res) => setTimeout(res, 2500));
            try {
              const fresh = await api.get(`/sessions/${sid}`);
              const ftl = Array.isArray(fresh.data?.dialogue_timeline) ? fresh.data.dialogue_timeline : null;
              if (ftl && ftl.length && _openingOf(ftl)?.audio_hash) {
                timeline = ftl;
                strace("FULL_TIMELINE", "opening cluster audio arrived — activating", {
                  waited_ms: Date.now() - _waitStart,
                });
                break;
              }
            } catch (e) { /* transient poll error — keep waiting */ }
          }
          if (!_openingOf(timeline)?.audio_hash) {
            strace("FULL_TIMELINE", "opening audio wait timed out — falling back to legacy render path", {
              waited_ms: Date.now() - _waitStart,
            });
          }
        }
        if (timeline && timeline.length && timeline.some((s) => s.audio_hash)) {
          dialogueTimelineRef.current = timeline;
          hasFullTimelineRef.current = true;
          // Iter 82 — collect the chained opening clusters (played via the
          // chain player after the first cluster's <audio> element ends).
          openingChainRef.current = timeline
            .filter((s) => s.kind === "opening_cont")
            .sort((a, b) => (a.cluster_index || 0) - (b.cluster_index || 0));
          openingChainIndexRef.current = 0;
          openingChainStateRef.current = "idle";
          // Iter 104 — in full-timeline mode each segment owns its cues
          // (armSegmentActionCues). The legacy audioRef-anchored scheduler
          // must only see the FIRST opening cluster's cues, otherwise it
          // misfires every later cue against the first cluster's duration.
          const _openingSeg = timeline.find((s) => s.kind === "opening") || timeline[0];
          actionCuesRef.current = Array.isArray(_openingSeg?.action_cues) ? _openingSeg.action_cues : [];
          strace("FULL_TIMELINE", "activated — reactive queue disabled", {
            segment_count: timeline.length,
            rendered: timeline.filter((s) => s.audio_hash).length,
            first_opening_at_sec: timeline[0]?.at_sec,
            opening_clusters: 1 + openingChainRef.current.length,
            reactions: timeline.filter((s) => s.kind === "reaction").length,
            quiet_beats: timeline.filter((s) => s.kind === "quiet_beat").length,
          });
        } else if (timeline && timeline.length) {
          // Iter 82 — never fail silently: make the legacy fallback visible.
          strace("FULL_TIMELINE", "skipped — timeline present but no rendered segments (legacy single-MP3 path)", {
            segment_count: timeline.length,
          });
        }
        if (data.morning_pending) {
          toast("The morning is being prepared.", { duration: 4500 });
        }

        // Mirror the URL so deep-linking + memory replay work the same way.
        try { window.history.replaceState(null, "", `/sessions/${sid}`); } catch (e) { /* ignore */ }

        // Update mixer's random events with the LLM's actual atmosphere tags.
        if (mixerRef.current && data.evening.sound_plan?.ambient_events) {
          mixerRef.current.scheduleAmbientEvents(data.evening.sound_plan.ambient_events);
        }

        // Synthesize the companion's voice.
        try {
          // Iteration 59 — if we have a full-timeline session, use the first
          // (opening) segment's pre-rendered audio URL directly and schedule
          // the remaining segments via setTimeout. No /render_audio call.
          if (hasFullTimelineRef.current) {
            const findOpening = () =>
              dialogueTimelineRef.current.find((s) => s.kind === "opening") || dialogueTimelineRef.current[0];
            let opening = findOpening();
            // Iter 113 — the pre-render worker starts AFTER status:ready, so the
            // opening's MP3 is often still seconds away. WAIT for it (polling the
            // session doc) instead of instantly abandoning timeline mode: the
            // legacy fallback re-renders the whole opening from scratch and was
            // the main "she never speaks" path when that render also failed.
            if (!(opening?.audio_url && opening?.audio_hash)) {
              strace("FULL_TIMELINE", "opening audio not rendered yet — waiting for pre-render worker");
              const openClock = makeActiveClock(45000); // Iter 125 — suspension-aware
              while (openClock.tick() && !endedRef.current) {
                await new Promise((res) => setTimeout(res, 2500));
                await refreshTimelineAudio();
                opening = findOpening();
                if (opening?.audio_url && opening?.audio_hash) break;
              }
              if (endedRef.current) return;
            }
            if (opening?.audio_url && opening?.audio_hash) {
              try { dialogueAudioHashRef.current = opening.audio_hash; } catch (e) { /* ignore */ }
              const _absUrl = `${API}${opening.audio_url.replace("/api", "")}`;
              await waitForMorningOpeningGate();
              if (endedRef.current) return;
              setAudioUrl(_absUrl);
              strace("FULL_TIMELINE", "opening segment audio set", { hash: opening.audio_hash, url: _absUrl });
              setPhase("settled");
              // Schedule every non-opening segment to play at its at_sec.
              scheduleTimelineSegments();
              // Skip legacy /render_audio path.
              return;
            } else {
              strace("FULL_TIMELINE", "opening audio never arrived after 45s wait — falling back to legacy render path");
              hasFullTimelineRef.current = false;
            }
          }
          strace("TTS", "POST /sessions/{id}/render_audio → starting", { session_id: sid });
          // Iter 76 — TTS pipeline stage instrumentation.
          console.log("[TTS_STAGE][STAGE=1/RenderJobQueued.request] session_id=" + sid);
          const _tts_t0 = performance.now();
          const r = await api.post(`/sessions/${sid}/render_audio`);
          const _tts_dt = Math.round(performance.now() - _tts_t0);
          strace("TTS", "response received", {
            took_ms: _tts_dt,
            hash: r.data.hash,
            bytes: r.data.bytes,
            url: r.data.url,
            ready: r.data.ready,
            status_url: r.data.status_url,
          });
          console.log("[TTS_STAGE][STAGE=1/RenderJobQueued.response] session_id=" + sid + " hash=" + r.data.hash + " ready=" + r.data.ready + " took_ms=" + _tts_dt);
          // Phase 3 — remember the dialogue hash so the master renderer can
          // pick it out of the local audio_cache without re-downloading it.
          try { dialogueAudioHashRef.current = r.data.hash || null; } catch (e) { /* ignore */ }
          const _absUrl = `${API}${r.data.url.replace("/api", "")}`;
          if (r.data.ready) {
            // Cache hit — audio already on disk.
            await waitForMorningOpeningGate();
            if (endedRef.current) return;
            setAudioUrl(_absUrl);
            strace("PLAYER", "audioUrl set (cache hit)", { url: _absUrl });
            setPhase("settled");
          } else {
            // Background render kicked off — poll the status endpoint until
            // the audio is ready. This is what keeps the initial POST fast
            // enough to survive Cloudflare's ~100s ingress budget.
            strace("TTS", "background render started — polling status", {
              status_url: r.data.status_url,
              poll_interval_ms: r.data.poll_interval_ms,
            });
            const pollUrl = r.data.status_url;
            const pollInterval = r.data.poll_interval_ms || 1500;
            const ttsClock = makeActiveClock(130000); // Iter 125 — suspension-aware
            let readyBytes = 0;
            let _pollTick = 0;
            while (ttsClock.tick()) {
              await new Promise((res) => setTimeout(res, pollInterval));
              if (endedRef.current) return;
              _pollTick += 1;
              try {
                const st = await api.get(pollUrl.replace("/api", ""));
                // Iter 76 — log every poll tick so we can verify polling
                // continues past the initial ready:false response.
                console.log(
                  "[TTS_STAGE][STAGE=7/PollTick] hash=" + r.data.hash +
                  " tick=" + _pollTick +
                  " elapsed_ms=" + (Date.now() - _tts_t0) +
                  " status=" + (st.data?.status) +
                  " ready=" + (st.data?.ready) +
                  (st.data?.error ? " error=" + st.data.error : "")
                );
                if (st.data?.status === "done" && st.data?.ready) {
                  readyBytes = st.data.bytes || 0;
                  strace("TTS", "background render DONE", {
                    took_ms: st.data.took_ms,
                    bytes: readyBytes,
                    poll_elapsed_ms: Date.now() - _tts_t0,
                  });
                  console.log(
                    "[TTS_STAGE][STAGE=7/PollReceivedReadyTrue] hash=" + r.data.hash +
                    " tick=" + _pollTick + " bytes=" + readyBytes +
                    " total_poll_elapsed_ms=" + (Date.now() - _tts_t0)
                  );
                  break;
                }
                if (st.data?.status === "error") {
                  strace("TTS", "background render FAILED", {
                    error: st.data.error,
                    took_ms: st.data.took_ms,
                  });
                  console.error(
                    "[TTS_STAGE][STAGE=7/PollReceivedError] hash=" + r.data.hash +
                    " error=" + st.data.error
                  );
                  triggerInUniverseError("tts_bg_failed");
                  return;
                }
                // pending / unknown → keep polling
              } catch (pollErr) {
                strace("TTS", "poll error (transient)", {
                  status: pollErr?.response?.status,
                  message: pollErr?.message,
                });
                console.warn(
                  "[TTS_STAGE][STAGE=7/PollTransientError] hash=" + r.data.hash +
                  " tick=" + _pollTick +
                  " http=" + (pollErr?.response?.status) +
                  " msg=" + (pollErr?.message)
                );
                // Transient network error while polling — keep trying until deadline.
              }
            }
            if (!readyBytes) {
              strace("TTS", "background render POLL_TIMEOUT (never became ready within 130s)");
              console.error(
                "[TTS_STAGE][STAGE=7/PollTimeout] hash=" + r.data.hash +
                " total_ticks=" + _pollTick + " watchdog=130000ms"
              );
              triggerInUniverseError("tts_poll_timeout");
              return;
            }
            await waitForMorningOpeningGate();
            if (endedRef.current) return;
            setAudioUrl(_absUrl);
            strace("PLAYER", "audioUrl set (poll ready)", { url: _absUrl });
            setPhase("settled");
          }
        } catch (e) {
          strace("TTS", "FAILED", {
            status: e?.response?.status,
            detail: e?.response?.data?.detail,
            message: e?.message,
          });
          triggerInUniverseError("tts_render_failed");
          return;
        }
      } catch (err) {
        strace("LLM", "FAILED", {
          status: err?.response?.status,
          detail: err?.response?.data?.detail,
          message: err?.message,
        });
        // Any non-permanent server failure (502/503/504/timeout/network) → in-universe error.
        const status = err?.response?.status;
        if (!status || status >= 500 || status === 0 || status === 408) {
          triggerInUniverseError("generate_failed");
        } else {
          // Genuine 4xx (auth, validation) — surface a clearer non-immersive fallback.
          const _d = err?.response?.data?.detail;
          const detail = (typeof _d === "string" && _d) || _d?.message || "The fire wouldn't quite catch. Try again.";
          setError({ title: "Something's off tonight.", lines: [detail] });
          setPhase("error");
        }
      }
    })();

    // NOTE: intentionally NO cleanup-cancellation flag here. `triedGenRef`
    // already guarantees the generate flow runs exactly once even under
    // React 18 StrictMode's double-mount, and the post-await setState calls
    // are idempotent. Adding a `cancelled` flag here would set itself true
    // during StrictMode unmount and silently abort the entire flow when
    // /sessions/generate finally resolves (~30s later).
    return undefined;
  }, []);

  // ============= STEP 3 — voice fade-in when audio is ready =============
  useEffect(() => {
    if (!audioUrl || !audioRef.current) return undefined;
    const el = audioRef.current;
    strace("PLAYER", "priming <audio>.play()", { src: audioUrl });
    el.volume = 0;
    const playPromise = el.play();
    if (playPromise && playPromise.catch) {
      playPromise.catch((err) => {
        // Autoplay policy rejected the play — NEVER show a tap prompt.
        // Self-heal: retry quietly on a short interval AND on the next
        // natural interaction anywhere on the page.
        strace("PLAYER", "play() REJECTED (likely autoplay policy) — silent self-heal engaged", { message: String(err?.message || err) });
        retryOnNextGesture(acceptUserGesture);
        if (playRetryRef.current) clearInterval(playRetryRef.current);
        playRetryRef.current = setInterval(() => {
          if (dialogueStartedRef.current || endedRef.current) {
            clearInterval(playRetryRef.current);
            playRetryRef.current = null;
            return;
          }
          const a = audioRef.current;
          if (!a) return;
          a.play().then(() => {
            strace("PLAYER", "silent retry succeeded — dialogue started automatically");
            clearInterval(playRetryRef.current);
            playRetryRef.current = null;
          }).catch(() => { /* keep retrying */ });
        }, 1500);
        // Extend the watchdog while we wait for playback to recover.
        if (watchdogRef.current) clearTimeout(watchdogRef.current);
        watchdogRef.current = setTimeout(() => {
          if (!dialogueStartedRef.current && !endedRef.current) triggerInUniverseError("user_gesture_timeout");
        }, 60000);
      });
    }
    // 1.5s linear fade — ambient stays put, voice joins it.
    const startedAt = Date.now();
    const RAMP_MS = 1500;
    if (fadeRef.current) clearInterval(fadeRef.current);
    fadeRef.current = setInterval(() => {
      const t = Math.min(1, (Date.now() - startedAt) / RAMP_MS);
      if (audioRef.current) audioRef.current.volume = t * targetVolume;
      if (t >= 1) {
        clearInterval(fadeRef.current);
        fadeRef.current = null;
      }
    }, 40);
    // Ambience stays NEARLY CONSTANT while she speaks — the world doesn't pause
    // for dialogue. Only a barely-perceptible dip so the voice sits naturally.
    if (mixerRef.current) mixerRef.current.duck(0.9);
    setPhase("playing");
    return () => {
      if (fadeRef.current) clearInterval(fadeRef.current);
    };
  }, [audioUrl]);

  // Additional dialogue-element diagnostics — help find where the pipeline
  // breaks when the user reports "ambient started but Serine never spoke".
  const onAudioLoadedData = () => strace("PLAYER", "onLoadedData (audio bytes downloaded + decoded)");
  const onAudioCanPlay = () => strace("PLAYER", "onCanPlay (ready to play)");
  // Iteration 58 — scheduling hook. Fires once we know the dialogue MP3's
  // true duration so we can interpolate each Action Director cue's
  // scheduled play time. Idempotent per cue via actionCuePlayedRef.
  const onAudioLoadedMetadata = () => {
    const dur = audioRef.current?.duration;
    strace("PLAYER", "onLoadedMetadata", { duration_sec: dur });
    scheduleActionCues();
    // Iter 75 — attach 350ms fade-out so the opening dialogue exits softly
    // instead of hard-cutting when the MP3 ends.
    try { if (audioRef.current) attachDialogueFades(audioRef.current); } catch (e) { /* ignore */ }
  };

  const clearActionCueTimers = () => {
    for (const t of actionCueTimersRef.current) {
      try { clearTimeout(t); } catch (e) { /* ignore */ }
    }
    actionCueTimersRef.current = [];
  };

  // Iteration 59 — Segment scheduler.
  //
  // When session.dialogue_timeline is present with rendered per-segment
  // audio_url values, every non-opening segment is scheduled to play at its
  // at_sec via setTimeout. The opening segment is already loaded into the
  // <audio> element (`audioRef`) so this scheduler only handles beats AFTER
  // the opening. Segment playback:
  //   • ducks the ambient mixer briefly during the beat
  //   • records the segment on the master timeline for Phase 3 replay
  //   • never overlaps: audio.onended fires before the next segment starts
  //     (or the next timeout gates itself on !isSpeaking())
  const clearSegmentTimers = () => {
    for (const t of segmentTimersRef.current) {
      try { clearTimeout(t); } catch (e) { /* ignore */ }
    }
    segmentTimersRef.current = [];
    // Iteration 63 — presence cue timers travel with segment timers.
    for (const t of presenceCueTimersRef.current) {
      try { clearTimeout(t); } catch (e) { /* ignore */ }
    }
    presenceCueTimersRef.current = [];
  };
  const scheduleTimelineSegments = () => {
    const segments = dialogueTimelineRef.current || [];
    if (!segments.length) return;
    clearSegmentTimers();
    const nowElapsedMs = sessionStartMsRef.current
      ? ((typeof performance !== "undefined") ? performance.now() : Date.now()) - sessionStartMsRef.current
      : 0;
    let scheduled = 0;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const key = `${i}:${seg.at_sec}:${seg.audio_hash || ""}`;
      if (segmentPlayedRef.current.has(key)) continue;
      if (seg.kind === "opening") { segmentPlayedRef.current.add(key); continue; }
      // Iter 82 — chained opening clusters are owned by the chain player,
      // never by the at_sec scheduler.
      if (seg.kind === "opening_cont") { segmentPlayedRef.current.add(key); continue; }
      if (!seg.audio_hash || !seg.audio_url) continue;
      const at_ms = (typeof seg.at_sec === "number" ? seg.at_sec : 0) * 1000;
      const delayMs = Math.max(0, at_ms - nowElapsedMs);
      const t = setTimeout(() => {
        if (endedRef.current || pausedRef.current) return;
        if (segmentPlayedRef.current.has(key)) return;
        segmentPlayedRef.current.add(key);
        // Iter 94 — morning embrace release rides the dialogue itself.
        if (isMorning && !seg.expressive_only && MORNING_RELEASE_RE.test(seg.text || "")) {
          releaseMorningEmbrace("dialogue_cue");
        }
        try {
          const url = `${API}${seg.audio_url.replace("/api", "")}`;
          const a = new Audio(url);
          a.volume = DIALOGUE_VOLUME;
          reactionAudioRef.current = a;
          // Iter 75 — 350ms fade-out so timeline segments exit softly.
          try { attachDialogueFades(a); } catch (e) { /* ignore */ }
          // Iter 104 — this segment's action SFX play inside ITS OWN pause gaps.
          if (Array.isArray(seg.action_cues) && seg.action_cues.length) {
            armSegmentActionCues(a, seg.action_cues, `seg${i}`);
          }
          if (mixerRef.current) mixerRef.current.duck(0.9);
          a.onended = () => {
            if (mixerRef.current && !pausedRef.current && !endedRef.current) mixerRef.current.unduck(1.0);
          };
          a.onerror = () => {
            if (mixerRef.current) mixerRef.current.unduck(1.0);
          };
          a.play().catch(() => { if (mixerRef.current) mixerRef.current.unduck(1.0); });
          // Mirror to master timeline so replays keep the same at_sec.
          timelineEventsRef.current.push({
            type: "reactive",
            at_sec: _now_at_sec(),
            audio_hash: seg.audio_hash,
            url: null,
            text: (seg.text || "").slice(0, 200),
            source: `timeline_segment:${seg.kind || "mid"}`,
          });
          logAudioPlay(`timeline_segment:${seg.kind || "mid"}`, seg.text, { audio_hash: seg.audio_hash, push: false });
          strace("FULL_TIMELINE", "segment fired", {
            at_sec: seg.at_sec,
            kind: seg.kind,
            expressive_only: !!seg.expressive_only,
            text_preview: (seg.text || "").slice(0, 60),
          });
        } catch (e) {
          strace("FULL_TIMELINE", "segment play failed", { at_sec: seg.at_sec, message: String(e?.message || e) });
        }
      }, delayMs);
      segmentTimersRef.current.push(t);
      // Iteration 63 — Presence Cue.
      // Brighten the arrival-orb PRESENCE_LEAD_MS before the segment plays,
      // then fade it back PRESENCE_HOLD_MS after firing. Deterministic
      // because the timeline is known — no polling, no guesswork.
      const leadDelay = Math.max(0, delayMs - PRESENCE_LEAD_MS);
      const holdEnd = delayMs + PRESENCE_HOLD_MS;
      const onLead = setTimeout(() => {
        if (endedRef.current || pausedRef.current) return;
        setPresenceCueActive(true);
      }, leadDelay);
      const onHoldEnd = setTimeout(() => {
        if (endedRef.current) return;
        setPresenceCueActive(false);
      }, holdEnd);
      presenceCueTimersRef.current.push(onLead, onHoldEnd);
      scheduled++;
    }
    strace("FULL_TIMELINE", "segments scheduled", { scheduled, total: segments.length });
  };

  // ============= Iter 82 — Opening cluster chain =============
  // Re-fetch the session doc and merge freshly-rendered audio_hash/audio_url
  // values into our local timeline copy (segments are pre-rendered by a
  // background worker that patches the doc as MP3s complete).
  const refreshTimelineAudio = async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    try {
      const { data } = await api.get(`/sessions/${sid}`);
      const fresh = Array.isArray(data?.dialogue_timeline) ? data.dialogue_timeline : null;
      if (!fresh) return;
      const merge = (seg) => {
        const match = fresh.find(
          (f) => f.kind === seg.kind && f.cluster_index === seg.cluster_index && f.at_sec === seg.at_sec,
        ) || fresh.find((f) => f.kind === seg.kind && (f.text || "") === (seg.text || ""));
        if (match?.audio_hash && !seg.audio_hash) {
          seg.audio_hash = match.audio_hash;
          seg.audio_url = match.audio_url;
        }
      };
      (dialogueTimelineRef.current || []).forEach(merge);
      (openingChainRef.current || []).forEach(merge);
      strace("OPENING_CHAIN", "timeline audio refreshed", {
        chain_rendered: (openingChainRef.current || []).filter((s) => s.audio_hash).length,
        chain_total: (openingChainRef.current || []).length,
      });
    } catch (e) {
      strace("OPENING_CHAIN", "timeline refresh failed", { message: String(e?.message || e) });
    }
  };

  // Play the next chained opening cluster after its REAL silence gap. During
  // the gap only the world speaks — fire, rain, her breathing. That silence
  // is part of the performance, not dead air.
  const playNextOpeningCluster = () => {
    if (endedRef.current) return;
    const chain = openingChainRef.current || [];
    const idx = openingChainIndexRef.current;
    if (idx >= chain.length) {
      openingChainStateRef.current = "done";
      handleOpeningFinished();
      return;
    }
    const seg = chain[idx];
    const gapMs = Math.max(300, (typeof seg.gap_before_sec === "number" ? seg.gap_before_sec : 6) * 1000);
    openingChainStateRef.current = "waiting";
    strace("OPENING_CHAIN", "silence before next cluster", {
      cluster: seg.cluster_index,
      gap_sec: Math.round(gapMs / 100) / 10,
      silence_kind: seg.silence_kind_before || seg.silence_kind || null,
    });
    const t = setTimeout(async () => {
      if (endedRef.current || pausedRef.current) return;
      if (!seg.audio_url) {
        // Pre-render may still be in flight — refresh and retry (up to 4x),
        // then skip so the evening never stalls on one cluster.
        seg._retries = (seg._retries || 0) + 1;
        if (seg._retries <= 4) {
          await refreshTimelineAudio();
          if (endedRef.current || pausedRef.current) return;
          const retry = setTimeout(() => {
            if (!endedRef.current && !pausedRef.current) {
              openingChainStateRef.current = "idle";
              playNextOpeningClusterNow(seg, idx);
            }
          }, seg.audio_url ? 0 : 2500);
          segmentTimersRef.current.push(retry);
          return;
        }
        strace("OPENING_CHAIN", "cluster audio never arrived — skipped", { cluster: seg.cluster_index });
        openingChainIndexRef.current = idx + 1;
        playNextOpeningCluster();
        return;
      }
      playNextOpeningClusterNow(seg, idx);
    }, gapMs);
    segmentTimersRef.current.push(t);
  };

  const playNextOpeningClusterNow = (seg, idx) => {
    if (endedRef.current || pausedRef.current) return;
    if (!seg.audio_url) { playNextOpeningCluster(); return; }
    openingChainStateRef.current = "playing";
    logAudioPlay(`opening_cluster:${idx}`, seg.text, { audio_hash: seg.audio_hash || null, push: false });
    const url = `${API}${seg.audio_url.replace("/api", "")}`;
    const a = new Audio(url);
    a.volume = DIALOGUE_VOLUME;
    reactionAudioRef.current = a;
    try { attachDialogueFades(a); } catch (e) { /* ignore */ }
    // Iter 104 — this cluster's action SFX play inside ITS OWN baked pause gaps.
    if (Array.isArray(seg.action_cues) && seg.action_cues.length) {
      armSegmentActionCues(a, seg.action_cues, `oc${idx}`);
    }
    if (mixerRef.current) mixerRef.current.duck(0.9);
    let advanced = false;
    const advance = () => {
      if (advanced) return;
      advanced = true;
      if (mixerRef.current && !pausedRef.current && !endedRef.current) mixerRef.current.unduck(1.0);
      openingChainIndexRef.current = idx + 1;
      if (!pausedRef.current && !endedRef.current) playNextOpeningCluster();
    };
    a.onended = advance;
    a.onerror = () => {
      strace("OPENING_CHAIN", "cluster LOAD ERROR — skipped", { cluster: seg.cluster_index });
      advance();
    };
    // Mirror to the master timeline so replays place this cluster exactly
    // where it played (dialogue-gain path in master_renderer).
    timelineEventsRef.current.push({
      type: "reactive",
      at_sec: _now_at_sec(),
      audio_hash: seg.audio_hash || null,
      url: null,
    });
    strace("OPENING_CHAIN", "cluster playing", {
      cluster: seg.cluster_index,
      of: seg.cluster_count,
      text_preview: (seg.text || "").slice(0, 60),
    });
    a.play().then(() => {
      // MAIN-TIMELINE PLAYBACK FIX — playback truly started; mark the session
      // begun (resume-replay path reaches here without the legacy audioRef).
      if (!dialogueStartedRef.current) {
        dialogueStartedRef.current = true;
        if (dialogueAtSecRef.current == null) dialogueAtSecRef.current = _now_at_sec();
        strace("PLAYER", "dialogueStartedRef = true — via opening chain");
        if (sessionIdRef.current) {
          api.post(`/sessions/${sessionIdRef.current}/playback-started`).catch(() => { /* never blocks playback */ });
        }
      }
    }).catch((err) => {
      // MAIN-TIMELINE PLAYBACK FIX — a rejected play() (backgrounded webview,
      // autoplay policy) used to silently advance(), burning through the WHOLE
      // opening chain with zero audible output. Stay on THIS cluster and retry
      // on the next foreground/gesture instead.
      strace("OPENING_CHAIN", "cluster play REJECTED — armed for retry (NOT skipped)", {
        cluster: seg.cluster_index,
        error: String(err?.message || err),
      });
      if (mixerRef.current && !pausedRef.current && !endedRef.current) mixerRef.current.unduck(1.0);
      openingChainIndexRef.current = idx; // stay on this cluster
      openingChainStateRef.current = "waiting";
      const retry = () => {
        document.removeEventListener("visibilitychange", retry);
        document.removeEventListener("pointerdown", retry);
        if (endedRef.current || pausedRef.current) return;
        if (openingChainIndexRef.current === idx && openingChainStateRef.current === "waiting") {
          playNextOpeningClusterNow(seg, idx);
        }
      };
      document.addEventListener("visibilitychange", retry);
      document.addEventListener("pointerdown", retry);
    });
  };

  // Iter 104 — SEGMENT-ANCHORED ACTION CUES. The aggregate action_cues list
  // used to be scheduled against the FIRST opening cluster's <audio> duration,
  // so a coiling cue living in cluster 10 (at ~5min) fired ~15s in, buried
  // under speech, and its real baked-in silent gap played with no SFX. Each
  // segment now arms its OWN cues against its OWN element via timeupdate
  // (pause/resume safe: timeupdate stops while the element is paused).
  const armSegmentActionCues = (el, cues, keyPrefix) => {
    if (!el || !cues || !cues.length) return;
    const pending = cues
      .map((cue, i) => ({ cue, key: `${keyPrefix}:${i}:${cue.category}` }))
      .filter(({ key, cue }) => cue.sfx_url && !actionCuePlayedRef.current.has(key));
    if (!pending.length) return;
    strace("ACTION_DIRECTOR", "segment cues armed", {
      segment: keyPrefix,
      categories: pending.map((p) => p.cue.category),
    });
    const onTime = () => {
      const dur = el.duration;
      if (!dur || !isFinite(dur) || dur <= 0) return;
      let allDone = true;
      for (const p of pending) {
        if (actionCuePlayedRef.current.has(p.key)) continue;
        const ratio = typeof p.cue.text_offset_ratio === "number" ? p.cue.text_offset_ratio : 0;
        const at = Math.max(0, Math.min(dur - 0.05, ratio * dur));
        if (el.currentTime + 0.3 >= at) {
          actionCuePlayedRef.current.add(p.key);
          try {
            // Iter 105 — presence SFX route through the mixer's intimate path
            // (warmth + slight stereo position immediately beside the listener).
            const vol = (typeof p.cue.volume === "number" && p.cue.volume > 0) ? p.cue.volume : 0.9;
            if (mixerRef.current) {
              mixerRef.current.playOneShot(p.cue.sfx_url, vol, { intimate: true });
            } else {
              const sfx = new Audio(p.cue.sfx_url);
              sfx.volume = vol;
              sfx.play().catch(() => { /* silent fail — the pause still exists in the audio */ });
            }
            // Iter 105 — coiling = she is wrapped around the listener now:
            // enter the cuddle state (recurring subtle physical presence).
            if (p.cue.category === "coiling" && mixerRef.current) mixerRef.current.enterCuddleState();
            timelineEventsRef.current.push({
              type: "action",
              at_sec: _now_at_sec(),
              url: p.cue.sfx_url,
              category: p.cue.category,
            });
            strace("ACTION_DIRECTOR", "segment sfx fired", {
              segment: keyPrefix,
              category: p.cue.category,
              at_in_segment_sec: Math.round(at * 100) / 100,
              pause_sec: p.cue.pause_sec,
            });
          } catch (e) {
            strace("ACTION_DIRECTOR", "segment sfx FAILED", { category: p.cue.category, message: String(e?.message || e) });
          }
        } else {
          allDone = false;
        }
      }
      if (allDone) el.removeEventListener("timeupdate", onTime);
    };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", () => el.removeEventListener("timeupdate", onTime), { once: true });
  };

  const scheduleActionCues = () => {
    const cues = actionCuesRef.current || [];
    if (!cues.length) return;
    const el = audioRef.current;
    const dur = el?.duration;
    if (!dur || !isFinite(dur) || dur <= 0) {
      strace("ACTION_DIRECTOR", "skip schedule — dialogue duration unknown yet");
      return;
    }
    clearActionCueTimers();
    for (let i = 0; i < cues.length; i++) {
      const cue = cues[i];
      const cueKey = `${i}:${cue.category}:${cue.sfx_url}`;
      if (actionCuePlayedRef.current.has(cueKey)) continue;
      const ratio = typeof cue.text_offset_ratio === "number" ? cue.text_offset_ratio : 0;
      const at_in_dialogue = Math.max(0, Math.min(dur - 0.05, ratio * dur));
      const currentT = el.currentTime || 0;
      const msUntilTrigger = Math.max(0, (at_in_dialogue - currentT) * 1000);
      const t = setTimeout(() => {
        if (endedRef.current || pausedRef.current) return;
        if (actionCuePlayedRef.current.has(cueKey)) return;
        actionCuePlayedRef.current.add(cueKey);
        try {
          // Iter 105 — presence SFX route through the mixer's intimate path.
          const vol = (typeof cue.volume === "number" && cue.volume > 0) ? cue.volume : 0.9;
          if (mixerRef.current) {
            mixerRef.current.playOneShot(cue.sfx_url, vol, { intimate: true });
          } else {
            const a = new Audio(cue.sfx_url);
            a.volume = vol;
            a.play().catch(() => { /* silent fail — the pause still exists in the audio */ });
          }
          // Iter 105 — coiling = cuddled state begins.
          if (cue.category === "coiling" && mixerRef.current) mixerRef.current.enterCuddleState();
          // Mirror to the master timeline so replays keep the same at_sec.
          timelineEventsRef.current.push({
            type: "action",
            at_sec: _now_at_sec(),
            url: cue.sfx_url,
            category: cue.category,
          });
          strace("ACTION_DIRECTOR", "sfx fired", {
            category: cue.category,
            at_in_dialogue_sec: Math.round(at_in_dialogue * 100) / 100,
            pause_sec: cue.pause_sec,
            duration_sec: cue.sfx_duration_sec,
          });
        } catch (e) {
          strace("ACTION_DIRECTOR", "sfx play FAILED", { category: cue.category, message: String(e?.message || e) });
        }
      }, msUntilTrigger);
      actionCueTimersRef.current.push(t);
    }
    strace("ACTION_DIRECTOR", "cues scheduled", {
      count: cues.length,
      dialogue_duration_sec: Math.round(dur * 100) / 100,
    });
  };
  const onAudioError = (e) => {
    const err = e?.target?.error;
    strace("PLAYER", "onError", {
      code: err?.code,
      message: err?.message,
      src: e?.target?.src,
      networkState: e?.target?.networkState,
      readyState: e?.target?.readyState,
    });
    if (!dialogueStartedRef.current) triggerInUniverseError("audio_element_error");
  };
  // Iter 115 — cancel generation entirely: the session never existed.
  const cancelGeneration = () => {
    endedRef.current = true;
    try { if (mixerRef.current) mixerRef.current.stop(); } catch (e) { /* ignore */ }
    try { if (audioRef.current) audioRef.current.pause(); } catch (e) { /* ignore */ }
    const sid = sessionIdRef.current;
    if (sid) api.post(`/sessions/${sid}/cancel`).catch(() => { /* best-effort */ });
    strace("SESSION", "generation CANCELLED by user", { session_id: sid });
    nav("/rest");
  };

  const acceptUserGesture = () => {
    strace("PLAYER", "gesture received — resuming blocked playback");
    setNeedsUserGesture(false);
    try {
      audioRef.current?.play().catch(() => { /* if this fails too, watchdog will handle it */ });
    } catch (e) { /* ignore */ }
  };

  // Lock-screen metadata — always matches the active character + atmosphere.
  useEffect(() => {
    setSessionMetadata({
      characterName: character?.name,
      atmosphereName: atmoName,
      kind: isMorning ? "morning" : "evening",
      // Now Playing / lock-screen artwork = the selected character's card
      // artwork (single source of truth from the catalog). Falls back to the
      // dark moon art until the catalog resolves. Static for now — swap this
      // URL for the animated variant later without touching anything else.
      artworkUrl: character?.image || "/lockscreen-dark.png",
    });
    return () => clearSessionMetadata();
  }, [character?.name, character?.image, atmoName]);

  // ============= TEARDOWN =============
  useEffect(() => {
    return () => {
      if (fadeRef.current) clearInterval(fadeRef.current);
      if (playRetryRef.current) { clearInterval(playRetryRef.current); playRetryRef.current = null; }
      if (endFadeTimerRef.current) clearTimeout(endFadeTimerRef.current);
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      // Iteration 58 — cancel pending action SFX timers on unmount.
      for (const t of actionCueTimersRef.current) { try { clearTimeout(t); } catch (e) { /* ignore */ } }
      actionCueTimersRef.current = [];
      // Iteration 59 — cancel pending pre-composed segment timers on unmount
      // so no dialogue playback survives a page navigation.
      for (const t of segmentTimersRef.current) { try { clearTimeout(t); } catch (e) { /* ignore */ } }
      segmentTimersRef.current = [];
      for (const t of morningTimersRef.current) { try { clearTimeout(t); } catch (e) { /* ignore */ } }
      morningTimersRef.current = [];
      fgService.stop();
      try { reactionAudioRef.current?.pause(); } catch (e) { /* ignore */ }
      if (mixerRef.current) { mixerRef.current.onEvent = null; mixerRef.current.stop(); }
      if (ambientAudioRef.current) {
        try {
          ambientAudioRef.current.pause();
          ambientAudioRef.current.srcObject = null;
        } catch (e) { /* ignore */ }
      }
      try { stopKeepAlive(); } catch (e) { /* ignore */ }
      clearSessionMetadata();
    };
  }, []);

  // ============= INTERACTION =============
  // The Play/Pause button controls the ENTIRE session — ambience, timer,
  // scheduled world events, dialogue, and future reactions — as one unit.
  const togglePlay = () => {
    if (endedRef.current) return;
    if (playing) {
      // Explicit user intent to pause — the recovery loop must respect this.
      userInitiatedPauseRef.current = true;
      pauseSession();
    } else {
      // Explicit user intent to resume — clear the flag before resuming so
      // any subsequent involuntary pause is treated as an interruption.
      userInitiatedPauseRef.current = false;
      resumeSession();
    }
  };

  const leave = async () => {
    endedRef.current = true;
    setGlobalSessionEnded(true);
    fgService.stop();
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    try { reactionAudioRef.current?.pause(); } catch (e) { /* ignore */ }
    if (mixerRef.current) { mixerRef.current.onEvent = null; await mixerRef.current.stop(); }
    closeHostedSession(); // Iter 86 — unmount the hosted session (stops all audio)
    nav("/app");
  };

  // Audio element events — dialogue is only ONE part of the session.
  const onAudioPlay = () => {
    strace("PLAYER", "onPlay fired");
    // Iter 76 — TTS pipeline stage instrumentation.
    // STAGE 10: playback started.
    try {
      const el = audioRef.current;
      console.log(
        "[TTS_STAGE][STAGE=10/PlaybackStarted] currentTime=" + (el?.currentTime) +
        " duration=" + (el?.duration) +
        " volume=" + (el?.volume) +
        " muted=" + (el?.muted) +
        " paused=" + (el?.paused)
      );
    } catch (e) { /* ignore */ }
    if (!dialogueStartedRef.current) {
      dialogueStartedRef.current = true;
      // Phase 3 — record the exact moment dialogue first reached the speakers
      // relative to session t=0. This anchors the master renderer's dialogue overlay.
      if (dialogueAtSecRef.current == null) {
        dialogueAtSecRef.current = _now_at_sec();
        strace("MASTER", "dialogue at_sec captured", { at_sec: dialogueAtSecRef.current });
      }
      strace("PLAYER", "dialogueStartedRef = true — SESSION HAS PROPERLY BEGUN");
      // Iter 92 — trial-consumption safety + MAIN-TIMELINE PLAYBACK FIX: the
      // backend records playback_confirmed for EVERY session kind so a resume
      // can tell whether the opening truly reached the speakers.
      if (sessionIdRef.current) {
        api.post(`/sessions/${sessionIdRef.current}/playback-started`).catch(() => { /* never blocks playback */ });
      }
      // Iteration 57 — the opening MP3 is playing for the user. Reactive TTS
      // may now be generated (single-flight, still guarded against overlapping
      // playback via isSpeaking()). Any events that landed while the opening
      // was locked have been dropped with [REACTIVE_TTS] skipped: initial_pending.
      if (ttsLockRef.current === "initial") {
        ttsLockRef.current = null;
        rtrace("initial_unlocked", { at_sec: dialogueAtSecRef.current });
        // If any drain is possible, kick it (queue was NOT populated while
        // the initial was locked because enqueueReactive rejects then, but
        // we still call it defensively in case a race snuck one in).
        processReactiveQueue();
      }
      // Watchdog has done its job — clear it so it can't misfire later.
      if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; strace("WATCHDOG", "cleared (dialogue reached speakers)"); }
      // NOW — and only now — start companion presence loops. Breathing /
      // hissing / slithering / soft footsteps only exist because she
      // successfully arrived. Doing this here prevents the failure mode
      // where ambient + presence played but Serine never spoke.
      if (!presenceStartedRef.current && mixerRef.current) {
        presenceStartedRef.current = true;
        try {
          if (pendingPresenceRef.current) {
            mixerRef.current.schedulePresenceEvents(pendingPresenceRef.current);
            strace("PRESENCE", "presence scheduler ARMED");
          }
          if (pendingScenarioRef.current?.length) {
            mixerRef.current.scheduleScenarioEvents(pendingScenarioRef.current);
            strace("SCENARIO", "scenario scheduler ARMED");
          }
        } catch (e) { strace("PRESENCE", "scheduler arm FAILED", { error: String(e?.message || e) }); }
      }
    }
    if (ambientAudioRef.current && ambientAudioRef.current.paused) {
      ambientAudioRef.current.play().catch(() => { /* ignore */ });
    }
    try { if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing"; } catch (e) { /* ignore */ }
  };
  const onAudioPause = () => {
    strace("PLAYER", "onPause fired");
    try { if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused"; } catch (e) { /* ignore */ }
    // Iter 118 — the HTMLAudioElement paused. If the user did not ask for
    // this (no UI pause, no lock-screen pause, session not ended, session
    // has actually started), the OS or browser suspended us — try to
    // resume immediately. Continua's core promise is that a session never
    // stops itself. If play() is rejected (autoplay policy, backgrounded
    // browser), the visibility / app-active listeners will retry on the
    // next lifecycle event.
    if (
      !endedRef.current &&
      !pausedRef.current &&
      !userInitiatedPauseRef.current &&
      audioRef.current &&
      !audioRef.current.ended &&
      dialogueStartedRef.current
    ) {
      strace("PLAYER", "involuntary pause — attempting immediate resume");
      const p = audioRef.current.play();
      if (p && p.catch) p.catch(() => {
        strace("PLAYER", "involuntary resume rejected — waiting for foreground");
      });
    }
  };
  const onAudioEnded = () => {
    // Iter 82 — if the opening was split into thought clusters, the first
    // cluster ending is NOT the end of the opening. Real silence follows,
    // then the chain player continues with the next cluster.
    if ((openingChainRef.current || []).length && openingChainStateRef.current === "idle") {
      strace("PLAYER", "onEnded — opening cluster 1 finished, silence then chain continues", {
        remaining_clusters: openingChainRef.current.length,
      });
      if (mixerRef.current && !pausedRef.current && !endedRef.current) mixerRef.current.unduck(1.0);
      playNextOpeningCluster();
      return;
    }
    handleOpeningFinished();
  };

  const handleOpeningFinished = () => {
    strace("PLAYER", "opening finished (all clusters played)");
    // The opening monologue finished — the companion does NOT disappear. She
    // simply becomes quiet. Ambience + world events continue; reactions may
    // surface. The SESSION keeps running until the timer expires.
    if (mixerRef.current) mixerRef.current.unduck(1.0);
    // Iter 94 — morning: the heartbeat does NOT stop when the opening ends.
    // The listener is still waking in her embrace; it only fades when she
    // naturally moves away mid-conversation (releaseMorningEmbrace below).
    // Physical closeness is now established (she has spoken and drawn near) —
    // if tonight's scene is a close chest-resting embrace, her heartbeat fades
    // in now. Never before dialogue, never during the opening ambience.
    if (!isMorning && heartbeatEnabledRef.current && heartbeatUrlRef.current && mixerRef.current && !pausedRef.current && !endedRef.current) {
      mixerRef.current.startHeartbeat(heartbeatUrlRef.current);
      strace("PRESENCE", "heartbeat armed (close embrace scene)");
    }
    if (!pausedRef.current && !endedRef.current) scheduleIdle();
    // Iteration 57 — the initial monologue playback is DEFINITIVELY over.
    // Any pending items that were enqueued during "still playing" gates
    // (isSpeaking() true) can now drain. This is the primary hand-off point
    // between the opening and the ongoing session's reactive presence.
    if (ttsLockRef.current === null) processReactiveQueue();
    try { if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing"; } catch (e) { /* ignore */ }
  };

  // Bind lock-screen / headphone / AirPods media controls to the whole session.
  useEffect(() => {
    bindMediaSessionControls({
      onPlay: () => {
        // Iter 118 — resume from lock-screen counts as explicit user intent
        // to keep playing; clear the flag so involuntary pauses can still
        // trigger the auto-recovery path.
        userInitiatedPauseRef.current = false;
        resumeSession();
      },
      onPause: () => {
        // Lock-screen / headphone pause tap IS an explicit user action —
        // honor it and keep the session paused until the user resumes.
        userInitiatedPauseRef.current = true;
        pauseSession();
      },
    });
  }, []);

  // Iter 118 — INVOLUNTARY-PAUSE RECOVERY.
  //
  // If we ended up paused WITHOUT an explicit user action (iOS suspended
  // the tab, the audio session was interrupted, WebAudio was throttled),
  // resume the moment the browser lets us. Fired on every visibility flip
  // to visible AND on the Capacitor foreground bridge. Session Lifecycle:
  // the only sticky pause states are `userInitiatedPauseRef=true` or an
  // ended session — everything else self-heals.
  useEffect(() => {
    const attemptRecovery = (source) => {
      if (endedRef.current) return;
      if (userInitiatedPauseRef.current) return;
      if (!pausedRef.current && audioRef.current && !audioRef.current.paused) return;
      if (!dialogueStartedRef.current) return; // pre-arrival: watchdog owns recovery
      strace("PLAYER", "involuntary-pause recovery", { source });
      resumeSession();
    };
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      attemptRecovery("visibilitychange");
    };
    const onAppActive = () => attemptRecovery("app-active");
    const onFocus = () => attemptRecovery("focus");
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("continua:app-active", onAppActive);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("continua:app-active", onAppActive);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // ============= UI =============
  return (
    <div className="min-h-screen relative text-violet-50 overflow-hidden" data-testid="arrival-screen">
      {/* Iter 115 — cancel generation: visible ✕ until dialogue actually starts.
          Cancelling behaves as if the session never existed. */}
      {!error && (
        <button
          type="button"
          onClick={cancelGeneration}
          data-testid="cancel-session-button"
          aria-label="Cancel session"
          className="fixed right-4 z-40 w-10 h-10 rounded-full flex items-center justify-center border border-violet-300/25 bg-black/30 backdrop-blur-md text-violet-200/80 hover:text-violet-50 hover:border-violet-300/50 transition-soft"
          style={{ top: "calc(env(safe-area-inset-top, 0px) + 1rem)" }}
        >
          ✕
        </button>
      )}
      <div className="absolute inset-0 grain-overlay pointer-events-none" />
      <TopBar onOpenSettings={() => setDrawerOpen(true)} />

      <div className="relative z-10 min-h-[calc(100vh-5rem)] flex flex-col items-center justify-center px-6 text-center pb-40">
        {/* Iter 87 — the "Quiet" back button is gone. Leaving the session view
            is done via the bottom nav; the session keeps playing globally. */}
        <div
          className="mb-10 flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-violet-300/80 fade-rise"
          data-testid="arrival-header"
        >
          <Sparkles className="w-3 h-3" />
          {error
            ? `${companionName} isn't here right now.`
            : phase === "playing"
              ? `${companionName} is here.`
              : `Looking for ${companionName}…`}
        </div>

        <div
          className={`w-24 h-24 rounded-full border overflow-hidden flex items-center justify-center mb-12 breathe transition-all duration-700 ease-out ${
            presenceCueActive
              ? "border-violet-100/80 bg-black shadow-[0_0_38px_10px_rgba(196,181,253,0.35)] scale-105"
              : "border-violet-300/40 bg-black"
          }`}
          data-testid="arrival-orb"
          data-presence-cue={presenceCueActive ? "on" : "off"}
        >
          {character?.image ? (
            // Selected character's card artwork — the character is visually
            // present through the whole loading/playing flow. Static for now;
            // the animated variant can replace this <img> later.
            <img
              src={character.image}
              alt={character?.name || "Companion"}
              className="w-full h-full object-cover"
              data-testid="arrival-character-artwork"
            />
          ) : isMorning ? (
            <Sun className="w-8 h-8 text-violet-200" />
          ) : (
            <img
              src="/continua-logo-small.png"
              alt="Continua"
              className="w-14 h-14 object-contain"
              data-testid="playback-continua-logo"
            />
          )}
        </div>

        {!error && (
          <h1
            className="font-serif-display text-3xl md:text-5xl mb-4 max-w-2xl leading-tight"
            style={{ opacity: sessionEnded ? 1 : (phase === "playing" ? 1 : tipOpacity), transition: "opacity 700ms ease" }}
            data-testid="arrival-location"
          >
            {sessionEnded
              ? <>The evening has <span className="italic text-violet-200/90">come to a close</span>.</>
              : phase === "playing"
              ? <>You are <span className="italic text-violet-200/90">at the {atmoName.toLowerCase().replace(/^the\s+/, "")}</span>.</>
              : <span key={tipIndex}>{PREPARATION_TIPS[tipIndex]}</span>}
          </h1>
        )}

        {showLockHint && !sessionEnded && !error && (
          <div
            className="flex items-center gap-2 px-4 py-2 rounded-full border border-violet-200/20 bg-violet-200/6 text-violet-200/75 text-xs mb-4 fade-rise"
            data-testid="lockscreen-hint"
          >
            <Moon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
            <span>Continues under lock screen — you can safely lock your phone.</span>
          </div>
        )}

        {sessionEnded && (
          <p className="text-violet-300/70 text-sm max-w-sm mx-auto leading-relaxed fade-rise" data-testid="arrival-ended-note">
            {companionName} stayed with you the whole time. Rest well.
          </p>
        )}

        {sessionEnded && isMorning && morningFeedbackDue && (
          <MorningFeedback
            sessionId={sessionIdRef.current}
            onDone={() => setMorningFeedbackDue(false)}
          />
        )}

        {/* Iter 87 — reflection appears directly beneath the completed session.
            No separate screen; the session view stays visible. */}
        {sessionEnded && !(isMorning && morningFeedbackDue) && (
          <div className="mt-10 glass-strong rounded-2xl p-6 max-w-xl w-full text-left fade-rise" data-testid="session-reflection-panel">
            <div className="text-xs uppercase tracking-[0.28em] text-violet-300/90 mb-2">Optional</div>
            <h2 className="font-serif-display text-xl mb-2">Anything you would like next time?</h2>
            <p className="text-violet-50/80 text-sm mb-4">No stars, no ratings — just a leaning. {companionName} will quietly remember.</p>
            <div className="flex flex-wrap gap-2">
              {FEEDBACK_TAGS.map((tag) => {
                const on = feedback.includes(tag.id);
                return (
                  <button
                    key={tag.id} type="button"
                    disabled={feedbackSent}
                    onClick={() => setFeedback((f) => on ? f.filter((t) => t !== tag.id) : [...f, tag.id])}
                    data-testid={`reflection-tag-${tag.id}`}
                    className={`text-sm rounded-full px-4 py-2 border transition-soft inline-flex items-center gap-1.5 ${on ? "border-violet-300/60 bg-violet-300/15 text-violet-50" : "border-violet-300/20 text-violet-50/85 hover:border-violet-300/45"} ${feedbackSent ? "opacity-50 cursor-default" : ""}`}
                  >
                    {on && <Check className="w-3 h-3" />} {tag.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              disabled={feedback.length === 0 || feedbackSent}
              onClick={async () => {
                try {
                  await api.post(`/sessions/${sessionIdRef.current}/feedback`, { tags: feedback });
                  setFeedbackSent(true);
                  toast.success("They will keep that in mind.");
                } catch {
                  toast.error("Couldn't send. Try again.");
                }
              }}
              data-testid="reflection-submit-button"
              className="mt-5 inline-flex items-center gap-2 rounded-full px-6 py-2 bg-violet-200 text-indigo-950 font-medium hover:bg-violet-100 transition-soft disabled:opacity-40"
            >
              {feedbackSent ? "Shared" : "Share this with them"}
            </button>
          </div>
        )}

        {phase !== "playing" && !error && !sessionEnded && (
          <p
            className="text-violet-300/65 text-sm max-w-sm mx-auto leading-relaxed fade-rise"
            data-testid="arrival-preparation-tip"
          >
            The world is settling softly around you.
          </p>
        )}

        {memoryTitle && phase === "playing" && (
          <p
            className="text-violet-200/85 italic font-serif-display text-lg md:text-xl mt-2 fade-rise"
            data-testid="arrival-memory-title"
          >
            {memoryTitle}
          </p>
        )}

        {error && (
          <div className="mt-2 max-w-md text-center" data-testid="arrival-error">
            <p className="font-serif-display text-2xl md:text-3xl text-violet-100 mb-5 leading-tight" data-testid="arrival-error-title">
              {error.title}
            </p>
            <div className="space-y-2 text-violet-300/75 text-sm leading-relaxed" data-testid="arrival-error-body">
              {(error.lines || []).map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
            <button
              type="button"
              onClick={leave}
              data-testid="arrival-error-back"
              className="mt-8 rounded-full px-6 py-2 border border-violet-300/30 text-violet-200 hover:border-violet-300/60 hover:text-violet-50 transition-soft"
            >
              Back home
            </button>
          </div>
        )}
      </div>

      {/* Hidden audio element — auto-plays with fade-in once URL is ready */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="auto"
          autoPlay
          onPlay={onAudioPlay}
          onPause={onAudioPause}
          onEnded={onAudioEnded}
          onLoadedData={onAudioLoadedData}
          onCanPlay={onAudioCanPlay}
          onLoadedMetadata={onAudioLoadedMetadata}
          onError={onAudioError}
          data-testid="arrival-audio"
        />
      )}

      {/* Autoplay recovery is fully silent (see STEP 3) — no visible prompt. */}
      {/* Hidden ambient mirror — keeps the WebAudio bed playing under the lock
          screen by feeding the AudioContext's MediaStream into an <audio>
          element (which iOS / Android do NOT suspend in the background). */}
      <audio
        ref={ambientAudioRef}
        autoPlay
        playsInline
        style={{ display: "block", position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0, pointerEvents: "none" }}
        data-testid="arrival-ambient-audio"
      />

      {/* Floating pause/resume — the only control; hidden once the session ends */}
      {!error && !sessionEnded && (
        <div
          className="fixed bottom-0 left-0 right-0 z-20 px-6 pointer-events-none"
          style={{ paddingBottom: "calc(7rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <div className="max-w-md mx-auto flex justify-center pointer-events-auto">
            <button
              type="button"
              onClick={togglePlay}
              data-testid="arrival-toggle-button"
              aria-label={playing ? "Pause" : "Resume"}
              className={`shrink-0 w-14 h-14 rounded-full flex items-center justify-center transition-soft ${
                playing
                  ? "bg-violet-300/85 text-indigo-950 hover:bg-violet-200"
                  : "bg-violet-200 text-indigo-950 hover:bg-violet-100"
              } glow-violet`}
            >
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
          </div>
        </div>
      )}
      <BottomNav />
      <SettingsDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
}
